import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import AssetGridGallery from '../components/AssetGridGallery';
import AuthPanel from '../components/AuthPanel';
import ShareFormRenderer from '../components/ShareFormRenderer';
import defaultLinkIcon from '../assets/icons/icon.png';
import sharablLogo from '../assets/images/logo.png';
import { formService } from '../services/formService';
import { interactionService } from '../services/interactionService';
import { itemFormService } from '../services/itemFormService';
import { linkAnalyticsService } from '../services/linkAnalyticsService';
import { orderService } from '../services/orderService';
import { registerUpdateService } from '../services/registerUpdateService';
import { shareService } from '../services/shareService';
import { normaliseItemForms } from '../utils/itemForms';
import { assetsForGrid, normaliseAssetGrids, normaliseGridAccessLevel } from '../utils/assetGrids';
import { currencySymbol, formatPrice, getEffectivePrice, parsePrice } from '../utils/currency';
import { normalisePhoneNumber, phoneNumberForWhatsApp } from '../utils/phoneNumbers';
import { renderFontFamily } from '../utils/renderFonts';
import { DEFAULT_SHARE_ORIGIN, buildAssetShareUrl, buildClientShareData, buildShareUrl, decodeShareAssetId } from '../utils/shareLinks';

const setMetaContent = (selector, content, createAttributes = {}) => {
  if (!content) return;
  let element = document.head.querySelector(selector);

  if (!element && Object.keys(createAttributes).length) {
    element = document.createElement('meta');
    Object.entries(createAttributes).forEach(([name, value]) => {
      element.setAttribute(name, value);
    });
    document.head.appendChild(element);
  }

  element?.setAttribute('content', content);
};

const toAbsoluteUrl = (value) => {
  if (!value) return '';

  try {
    return new URL(value, window.location.href).toString();
  } catch (error) {
    return '';
  }
};

const markdownToPlainText = (value = '') => String(value)
  .replace(/!\[[^\]]*]\([^)]*\)/g, '')
  .replace(/\[([^\]]+)]\([^)]*\)/g, '$1')
  .replace(/[`*_~>#-]/g, '')
  .replace(/\s+/g, ' ')
  .trim();

const imageUrlForAsset = (asset = {}) => {
  const directImage = asset.preview || asset.downloadUrl || asset.iconUrl || asset.listingLogoUrl || '';
  if (directImage) return directImage;

  const galleryImage = Array.isArray(asset.images)
    ? asset.images.find((image) => image.url || image.downloadUrl || image.preview)
    : null;
  return galleryImage?.url || galleryImage?.downloadUrl || galleryImage?.preview || '';
};

const metaForAsset = (link, asset) => {
  if (!asset) {
    return {
      description: link.description || 'Controlled branded links for documents, photos, albums, and product catalogs.',
      image: link.ogImage || link.logo || '',
      title: link.title || 'Sharabl asset link',
    };
  }

  const description = [
    markdownToPlainText(asset.description),
    asset.price ? `Price: ${asset.currency || link.currency || 'ZAR'} ${asset.price}` : '',
    asset.area ? `Area: ${asset.area}` : '',
    asset.quantity ? `Available: ${asset.quantity}` : '',
  ].filter(Boolean).join(' | ');

  return {
    description: description || link.description || 'View this item on Sharabl.',
    image: imageUrlForAsset(asset) || link.ogImage || link.logo || '',
    title: asset.title || asset.name || link.title || 'Sharabl asset link',
  };
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const dateTimeLocalValue = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const offsetDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  return offsetDate.toISOString().slice(0, 16);
};

const publicPageWidthClasses = {
  '7xl': 'max-w-7xl',
  '6xl': 'max-w-6xl',
  '5xl': 'max-w-5xl',
  '4xl': 'max-w-4xl',
  '3xl': 'max-w-3xl',
};

const isValidHexColor = (value = '') => /^#(?:[0-9a-f]{3}){1,2}$/i.test(value.trim());

const clampLogoHeight = (value, fallback = 48) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return fallback;
  return Math.min(Math.max(Math.round(numericValue), 24), 120);
};

const defaultCheckout = {
  enableRestaurantOrdering: false,
  enableOnlineCheckout: true,
  enablePosMode: false,
  cartButtonLabel: 'Add to cart',
  drawerEyebrow: 'Store checkout',
  drawerTitle: 'Cart',
  emptyCartMessage: 'Add store items to start an order.',
  customerDetailsLabel: 'Customer details',
  formTitle: 'Send order',
  nameLabel: 'Name',
  emailLabel: 'Email',
  phoneLabel: 'Phone',
  notesLabel: 'Notes',
  addressLabel: 'Delivery address',
  tableNumberLabel: 'Table number',
  deliveryFee: '0',
  enableOfflinePaymentDetails: false,
  bankName: '',
  accountName: '',
  accountNumber: '',
  branchCode: '',
  accountType: '',
  paymentInstructions: '',
  submitLabel: 'Send order',
  successMessage: 'Order sent.',
};

const registerUpdateTypes = ['Record', 'Availability', 'Utilisation', 'Inspection', 'Maintenance'];
const registerStatusOptions = [
  { value: 'R', label: 'R', title: 'Red', className: 'border-red-300 bg-red-600 text-white hover:bg-red-700' },
  { value: 'O', label: 'O', title: 'Orange', className: 'border-orange-300 bg-orange-500 text-white hover:bg-orange-600' },
  { value: 'G', label: 'G', title: 'Green', className: 'border-emerald-300 bg-emerald-600 text-white hover:bg-emerald-700' },
];

const defaultRegisterUpdateDraft = () => ({
  updateType: 'Record',
  dateTime: dateTimeLocalValue(),
  quantity: '',
  status: '',
  message: '',
});

const defaultItemFormOrderDraft = () => ({
  requiredBy: '',
  status: 'Requested',
  notes: '',
});

const registerStatusOptionFor = (value = '') => registerStatusOptions.find((option) => option.value === value);

const normaliseOptionList = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  return String(value || '')
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const bookingFieldId = (field = {}) => String(field.id || '').replace(/-\d{10,}-\d+$/, '');
const CHECK_IN_FIELD_ID = 'check-in-date';
const CHECK_OUT_FIELD_ID = 'check-out-date';
const AREA_FIELD_ID = 'area';
const ADULT_COUNT_FIELD_ID = 'adult-count';
const KIDS_COUNT_FIELD_ID = 'kids-count';
const ROOMS_FIELD_ID = 'rooms';
const BEDS_FIELD_ID = 'beds';
const SERVICE_CATEGORY_FIELD_ID = 'service-category';
const SERVICE_TYPE_FIELD_ID = 'service-type';
const SERVICE_DATE_FIELD_ID = 'service-date';
const SERVICE_TIME_FIELD_ID = 'service-time';
const SERVICE_ADDRESS_FIELD_ID = 'service-address';
const PICKUP_ADDRESS_FIELD_ID = 'pickup-address';
const DROPOFF_ADDRESS_FIELD_ID = 'dropoff-address';
const BOOKING_FILTER_FIELD_IDS = new Set([
  AREA_FIELD_ID,
  CHECK_IN_FIELD_ID,
  CHECK_OUT_FIELD_ID,
  ADULT_COUNT_FIELD_ID,
  KIDS_COUNT_FIELD_ID,
  ROOMS_FIELD_ID,
  BEDS_FIELD_ID,
  SERVICE_CATEGORY_FIELD_ID,
  SERVICE_TYPE_FIELD_ID,
  SERVICE_DATE_FIELD_ID,
  SERVICE_TIME_FIELD_ID,
  SERVICE_ADDRESS_FIELD_ID,
  PICKUP_ADDRESS_FIELD_ID,
  DROPOFF_ADDRESS_FIELD_ID,
]);

const bookingCategoryLabels = {
  accommodation: 'Accommodation',
  'physical-services': 'Physical services',
  services: 'Physical services',
  'digital-services': 'Digital services',
};

const serviceOptionsByCategory = {
  Home: ['Cleaning', 'Gardening', 'Pool cleaning', 'Plumbing', 'Electrical', 'Handyman'],
  Car: ['Car service', 'Vehicle diagnostics', 'Vehicle repairs', 'Tyres', 'Battery', 'Wash and detailing'],
  Office: ['Cleaning', 'Office equipment repair', 'Plumbing', 'Electrical', 'Handyman'],
  Transport: ['Delivery', 'Moving', 'Removal', 'Furniture transport', 'Courier'],
};

const addDaysToDateValue = (value, days) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';

  date.setDate(date.getDate() + days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const todayDateValue = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const bookingField = (field, changes = {}) => ({
  ...field,
  ...changes,
  id: changes.id || bookingFieldId(field),
});

const normaliseBookingFields = (fields = []) => {
  const normalisedFields = [];
  let hasAdults = false;
  let hasKids = false;

  fields.forEach((field) => {
    const canonicalId = bookingFieldId(field);

    if (canonicalId === 'adult-count') hasAdults = true;
    if (canonicalId === 'kids-count') hasKids = true;

    if (canonicalId === 'guest-count') {
      if (!hasAdults) {
        normalisedFields.push(bookingField(field, {
          id: 'adult-count',
          label: 'Adults',
          placeholder: field.placeholder || '2',
          required: true,
          type: 'number',
        }));
        hasAdults = true;
      }
      if (!hasKids) {
        normalisedFields.push({
          id: 'kids-count',
          label: 'Kids 12 and under',
          type: 'number',
          required: false,
          placeholder: '0',
        });
        hasKids = true;
      }
      return;
    }

    normalisedFields.push(bookingField(field));
  });

  return normalisedFields;
};

const accommodationBookingFilterFields = [
  { id: AREA_FIELD_ID, label: 'Area', type: 'text', required: true, placeholder: 'Preferred area' },
  { id: CHECK_IN_FIELD_ID, label: 'Check-in date', type: 'date', required: true, placeholder: '' },
  { id: CHECK_OUT_FIELD_ID, label: 'Check-out date', type: 'date', required: true, placeholder: '' },
  { id: ADULT_COUNT_FIELD_ID, label: 'Adults', type: 'number', required: true, placeholder: '2' },
  { id: KIDS_COUNT_FIELD_ID, label: 'Kids 12 and under', type: 'number', required: false, placeholder: '0' },
  { id: ROOMS_FIELD_ID, label: 'Rooms', type: 'number', required: true, placeholder: '1' },
  { id: BEDS_FIELD_ID, label: 'Beds', type: 'number', required: false, placeholder: '1' },
];

const ensureAccommodationBookingFilterFields = (fields = []) => {
  const fieldIds = new Set(fields.map((field) => field.id));
  return [
    ...accommodationBookingFilterFields.filter((field) => !fieldIds.has(field.id)),
    ...fields,
  ];
};

const CartIcon = () => (
  <svg
    aria-hidden="true"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <circle cx="8" cy="21" r="1" />
    <circle cx="19" cy="21" r="1" />
    <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
  </svg>
);

const CalendarIcon = () => (
  <svg
    aria-hidden="true"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <rect width="18" height="18" x="3" y="4" rx="2" />
    <path d="M3 10h18" />
  </svg>
);

const SquareCheckIcon = () => (
  <svg
    aria-hidden="true"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const SearchIcon = () => (
  <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const UpdatesIcon = () => (
  <svg
    aria-hidden="true"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path d="M12 3v4" />
    <path d="M12 17v4" />
    <path d="M5.64 5.64 8.46 8.46" />
    <path d="m15.54 15.54 2.82 2.82" />
    <path d="M3 12h4" />
    <path d="M17 12h4" />
    <path d="m5.64 18.36 2.82-2.82" />
    <path d="m15.54 8.46 2.82-2.82" />
  </svg>
);

const LinkIcon = () => (
  <svg
    aria-hidden="true"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const PhoneIcon = () => (
  <svg
    aria-hidden="true"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.35 1.9.66 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.31 1.85.53 2.81.66A2 2 0 0 1 22 16.92Z" />
  </svg>
);

const MessageIcon = () => (
  <svg
    aria-hidden="true"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />
  </svg>
);

const MailIcon = () => (
  <svg
    aria-hidden="true"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-10 6L2 7" />
  </svg>
);

function PublicSharePage({ error, link, onAccessGranted, onCreateLink, user }) {
  const [isResponseFormOpen, setIsResponseFormOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isItemFormOpen, setIsItemFormOpen] = useState(false);
  const [isRegisterUpdateOpen, setIsRegisterUpdateOpen] = useState(false);
  const [isAllRegisterUpdatesOpen, setIsAllRegisterUpdatesOpen] = useState(false);
  const [activeRegisterAsset, setActiveRegisterAsset] = useState(null);
  const [registerUpdates, setRegisterUpdates] = useState([]);
  const [allRegisterUpdates, setAllRegisterUpdates] = useState([]);
  const [registerUpdateDraft, setRegisterUpdateDraft] = useState(defaultRegisterUpdateDraft);
  const [registerUpdateStatus, setRegisterUpdateStatus] = useState('');
  const [registerUpdateError, setRegisterUpdateError] = useState('');
  const [isLoadingRegisterUpdates, setIsLoadingRegisterUpdates] = useState(false);
  const [isLoadingAllRegisterUpdates, setIsLoadingAllRegisterUpdates] = useState(false);
  const [isSubmittingRegisterUpdate, setIsSubmittingRegisterUpdate] = useState(false);
  const [activeItemFormId, setActiveItemFormId] = useState('');
  const [itemFormItems, setItemFormItems] = useState([]);
  const [itemFormOrderDraft, setItemFormOrderDraft] = useState(defaultItemFormOrderDraft);
  const [itemFormStatus, setItemFormStatus] = useState('');
  const [itemFormError, setItemFormError] = useState('');
  const [isSubmittingItemForm, setIsSubmittingItemForm] = useState(false);
  const [checkoutMode, setCheckoutMode] = useState('online');
  const [cartItems, setCartItems] = useState([]);
  const [bookingItems, setBookingItems] = useState([]);
  const [bookingAnswers, setBookingAnswers] = useState({});
  const [bookingMode, setBookingMode] = useState('booking');
  const [bookingFilter, setBookingFilter] = useState({
    area: '',
    checkInDate: '',
    checkOutDate: '',
    adults: '2',
    kids: '0',
    rooms: '1',
    beds: '1',
    serviceCategory: '',
    serviceType: '',
    serviceDate: '',
    serviceTime: '',
    serviceAddress: '',
    pickupAddress: '',
    dropoffAddress: '',
  });
  const [bookingStatus, setBookingStatus] = useState('');
  const [bookingError, setBookingError] = useState('');
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);
  const [isInquiryOpen, setIsInquiryOpen] = useState(false);
  const [activeInquiryAsset, setActiveInquiryAsset] = useState(null);
  const [inquiryAnswers, setInquiryAnswers] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [inquiryStatus, setInquiryStatus] = useState('');
  const [inquiryError, setInquiryError] = useState('');
  const [isSubmittingInquiry, setIsSubmittingInquiry] = useState(false);
  const [activePublicGridId, setActivePublicGridId] = useState('');
  const [activeGridSearch, setActiveGridSearch] = useState('');
  const [customerDraft, setCustomerDraft] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    deliveryAddress: '',
    tableNumber: '',
    paymentMethod: 'Cash',
  });
  const [checkoutStatus, setCheckoutStatus] = useState('');
  const [checkoutPaymentDetails, setCheckoutPaymentDetails] = useState(null);
  const [checkoutError, setCheckoutError] = useState('');
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [accessDraft, setAccessDraft] = useState({ email: '', password: '' });
  const [accessError, setAccessError] = useState('');
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [hasCredentialAccess, setHasCredentialAccess] = useState(false);
  const [inviteRequestDraft, setInviteRequestDraft] = useState({ email: '', phone: '', reason: '' });
  const [inviteRequestStatus, setInviteRequestStatus] = useState('');
  const [inviteRequestError, setInviteRequestError] = useState('');
  const [isSubmittingInviteRequest, setIsSubmittingInviteRequest] = useState(false);
  const [activeInviteOnlyForm, setActiveInviteOnlyForm] = useState('');
  const headerLogo = link?.logo || defaultLinkIcon;
  const formHeaderImage = link?.icon || link?.logo || defaultLinkIcon;
  const headerLogoIsDefault = headerLogo === defaultLinkIcon;
  const headerLogoHeight = clampLogoHeight(link?.logoHeight);
  const drawerLogoHeight = Math.min(headerLogoHeight, 48);
  const headerLogoImageStyle = {
    display: 'block',
    height: '100%',
    objectFit: 'contain',
    width: 'auto',
  };
  const drawerLogoContainerStyle = {
    height: `${drawerLogoHeight}px`,
  };
  const drawerLogoImageStyle = {
    display: 'block',
    height: '100%',
    objectFit: 'contain',
    width: 'auto',
  };
  const [isSquareHeaderLogo, setIsSquareHeaderLogo] = useState(null);
  const isPublicLink = link?.protection === 'Public link';
  const isInviteOnlyLink = link?.protection === 'Invite only';
  const isSignedInLinkOwner = Boolean(user?.uid && link?.ownerId && user.uid === link.ownerId);
  const userRole = user?.profile?.role || user?.role || '';
  const organisationRole = String(user?.profile?.organisationRole || '').toLowerCase();
  const canUseInternalLinkTools = Boolean(
    user?.uid &&
    link &&
    (
      user.uid === link.ownerId ||
      ['admin', 'manager'].includes(userRole) ||
      ['owner', 'admin', 'manager'].includes(organisationRole) ||
      (Array.isArray(link.adminIds) && link.adminIds.includes(user.uid)) ||
      (Array.isArray(link.managerIds) && link.managerIds.includes(user.uid))
    ),
  );
  const isDemoMode = link?.demoMode === true;
  const isWaitingListMode = link?.inviteRequestMode === 'waiting-list';
  const inviteEntryLabel = isWaitingListMode ? 'Waiting list' : 'Invite request';
  const inviteEntryActionLabel = isWaitingListMode ? 'Join waiting list' : 'Request invite';
  const inviteEntryTitle = isWaitingListMode ? 'Join the waiting list' : 'Request access';
  const inviteEntrySubmitLabel = isWaitingListMode ? 'Join waiting list' : 'Submit request';
  const inviteEntrySuccessMessage = isWaitingListMode ? 'You have joined the waiting list.' : 'Invite request sent.';
  const requiresCredentials = Boolean(link && !isPublicLink && !isSignedInLinkOwner);
  const accessStorageKey = link?.id ? `sharabl:share-access:${link.id}` : '';
  const accessRecordStorageKey = link?.id ? `sharabl:share-access-record:${link.id}` : '';

  useEffect(() => {
    if (!link) return;

    let favicon = document.head.querySelector('link[rel~="icon"]');
    const previousFaviconHref = favicon?.getAttribute('href') || '';
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.setAttribute('rel', 'icon');
      document.head.appendChild(favicon);
    }
    favicon.setAttribute('href', link.icon || defaultLinkIcon);

    const requestedAssetId = new URLSearchParams(window.location.search).get('asset') || '';
    const focusedAsset = requestedAssetId
      ? (link.assets || []).find((asset) => asset.id === requestedAssetId)
      : null;
    const meta = metaForAsset(link, focusedAsset);
    const title = meta.title;
    const description = meta.description;
    const shareUrl = focusedAsset
      ? buildAssetShareUrl(link, focusedAsset)
      : buildShareUrl(link);
    const image = toAbsoluteUrl(meta.image || '');

    document.title = `${title} | Sharabl`;
    setMetaContent('meta[name="description"]', description, { name: 'description' });
    setMetaContent('meta[property="og:title"]', title, { property: 'og:title' });
    setMetaContent('meta[property="og:description"]', description, { property: 'og:description' });
    setMetaContent('meta[property="og:url"]', shareUrl, { property: 'og:url' });
    setMetaContent('meta[name="twitter:card"]', image ? 'summary_large_image' : 'summary', { name: 'twitter:card' });
    setMetaContent('meta[name="twitter:title"]', title, { name: 'twitter:title' });
    setMetaContent('meta[name="twitter:description"]', description, { name: 'twitter:description' });

    if (image) {
      setMetaContent('meta[property="og:image"]', image, { property: 'og:image' });
      setMetaContent('meta[property="og:image:secure_url"]', image, { property: 'og:image:secure_url' });
      setMetaContent('meta[name="twitter:image"]', image, { name: 'twitter:image' });
      setMetaContent('meta[name="twitter:image:alt"]', title, { name: 'twitter:image:alt' });
    }

    return () => {
      if (previousFaviconHref) favicon.setAttribute('href', previousFaviconHref);
    };
  }, [link]);

  useEffect(() => {
    if (!link?.id || (requiresCredentials && !hasCredentialAccess)) return;

    linkAnalyticsService.trackUniqueView(link.id).catch((error) => {
      console.warn('Unable to track share view.', error);
    });
  }, [hasCredentialAccess, link?.id, requiresCredentials]);

  useEffect(() => {
    if (!accessStorageKey || !requiresCredentials) return;
    setHasCredentialAccess(window.sessionStorage.getItem(accessStorageKey) === 'granted');
  }, [accessStorageKey, requiresCredentials]);

  useEffect(() => {
    if (!hasCredentialAccess || !link?.isProtectedContentShell) return;
    onAccessGranted?.(link.slug);
  }, [hasCredentialAccess, link?.isProtectedContentShell, link?.slug, onAccessGranted]);

  useEffect(() => {
    if (headerLogoIsDefault) {
      setIsSquareHeaderLogo(true);
      return undefined;
    }

    let isCurrent = true;
    const image = new Image();

    image.onload = () => {
      if (!isCurrent) return;
      setIsSquareHeaderLogo(Math.abs(image.naturalWidth - image.naturalHeight) <= 2);
    };
    image.onerror = () => {
      if (!isCurrent) return;
      setIsSquareHeaderLogo(false);
    };
    image.src = headerLogo;

    return () => {
      isCurrent = false;
    };
  }, [headerLogo, headerLogoIsDefault]);

  useEffect(() => {
    if (!isResponseFormOpen && !isCartOpen && !isItemFormOpen && !isBookingOpen && !isRegisterUpdateOpen && !isAllRegisterUpdatesOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsResponseFormOpen(false);
        setIsCartOpen(false);
        setIsItemFormOpen(false);
        setIsBookingOpen(false);
        setIsRegisterUpdateOpen(false);
        setIsAllRegisterUpdatesOpen(false);
      }
    };
    const originalOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAllRegisterUpdatesOpen, isBookingOpen, isCartOpen, isItemFormOpen, isRegisterUpdateOpen, isResponseFormOpen]);

  if (!link) {
    return (
      <main className="grid min-h-screen place-items-center bg-[#f7f9fb] px-5 text-slate-950 antialiased">
        {error ? (
          <section className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 text-center">
            <p className="label-caps">Shared asset link</p>
            <h1 className="mt-2 text-2xl font-semibold tracking-normal">Link unavailable</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">{error}</p>
          </section>
        ) : (
          <section
            aria-label="Loading shared link"
            aria-live="polite"
            className="grid justify-items-center gap-5 text-center"
          >
            <div
              aria-hidden="true"
              className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-slate-950"
            />
            <img
              alt="Sharabl"
              className="h-12 w-auto"
              src={sharablLogo}
            />
            <span className="sr-only">Loading shared link</span>
          </section>
        )}
      </main>
    );
  }
  const shareUrl = buildShareUrl(link);
  const requestedAssetId = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('asset') ||
      (
        window.location.pathname
          .replace(/^\/+/, '')
          .replace(/\/+$/, '')
          .split('/')[0] === 'share-card'
          ? decodeShareAssetId(window.location.pathname.replace(/^\/+/, '').replace(/\/+$/, '').split('/')[2] || '')
          : ''
      )
    : '';
  const isExpired = Boolean(link.endsAt && new Date(link.endsAt).getTime() < Date.now());
  const visibleAssets = (link.assets || []).filter((asset) => (
    asset.active !== false
  ));
  const allPublicAssetGrids = normaliseAssetGrids({ ...link, assets: visibleAssets });
  const canAccessGrid = (grid = {}) => {
    if (canUseInternalLinkTools) return true;
    const accessLevel = normaliseGridAccessLevel(grid.accessLevel);
    if (accessLevel === 'public') return true;
    if (accessLevel === 'signed-in') return Boolean(user?.uid);
    if (accessLevel === 'invite-only') return Boolean(hasCredentialAccess);
    if (accessLevel === 'private') return false;
    return !requiresCredentials || hasCredentialAccess;
  };
  const publicAssetGrids = allPublicAssetGrids.filter(canAccessGrid);
  const publicGridAssetIds = new Set(publicAssetGrids.flatMap((grid) => grid.assetIds || []));
  const gridAccessibleAssets = visibleAssets.filter((asset) => publicGridAssetIds.has(asset.id));
  const focusedPublicAsset = requestedAssetId
    ? gridAccessibleAssets.find((asset) => asset.id === requestedAssetId)
    : null;
  const focusedPublicGrid = focusedPublicAsset
    ? publicAssetGrids.find((grid) => (grid.assetIds || []).includes(focusedPublicAsset.id))
    : null;
  const activePublicGrid = focusedPublicGrid ||
    publicAssetGrids.find((grid) => grid.id === activePublicGridId) ||
    publicAssetGrids[0];
  const activePublicGridAssets = assetsForGrid(gridAccessibleAssets, activePublicGrid);
  const activePublicGridCategory = activePublicGrid?.category || link.category || 'album';
  const isRegisterGrid = activePublicGridCategory === 'register';
  const hasRegisterGrid = link.category === 'register' ||
    publicAssetGrids.some((grid) => grid.category === 'register');
  const checkoutGridCategories = ['store', 'menu'];
  const activeGridUsesCheckout = checkoutGridCategories.includes(activePublicGridCategory);
  const gridScopedForm = !link.form?.gridId || link.form.gridId === activePublicGrid?.id ? link.form : null;
  const gridScopedFormEnabled = Boolean(gridScopedForm?.enabled && gridScopedForm?.fields?.length);
  const hasResponseForm = gridScopedFormEnabled;
  const isAccommodationBookingForm = Boolean(gridScopedFormEnabled && gridScopedForm?.preset === 'accommodation-booking');
  const normalisedGridBookingFields = normaliseBookingFields(gridScopedForm?.fields || []);
  const savedBookingCategory = ['accommodation', 'physical-services', 'digital-services'].includes(gridScopedForm?.bookingCategory)
    ? gridScopedForm.bookingCategory
    : '';
  const hasAccommodationBookingFields = normalisedGridBookingFields.some((field) => (
    [CHECK_IN_FIELD_ID, CHECK_OUT_FIELD_ID, ADULT_COUNT_FIELD_ID, ROOMS_FIELD_ID, BEDS_FIELD_ID].includes(field.id)
  ));
  const isAccommodationListingGrid = activePublicGridCategory === 'listing' && activePublicGrid?.listingType === 'accommodation';
  const bookingCategory = isAccommodationBookingForm && (isAccommodationListingGrid || hasAccommodationBookingFields)
    ? 'accommodation'
    : savedBookingCategory || 'physical-services';
  const isAccommodationBookingCategory = bookingCategory === 'accommodation';
  const isDigitalServiceBookingCategory = bookingCategory === 'digital-services';
  const isTransportServiceRequest = bookingFilter.serviceCategory === 'Transport';
  const bookingCategoryLabel = bookingCategoryLabels[bookingCategory] || 'Service';
  const responseFormButtonLabel = gridScopedForm?.buttonLabel?.trim() || 'Response form';
  const bookingFields = isAccommodationBookingForm && isAccommodationBookingCategory
    ? ensureAccommodationBookingFilterFields(normalisedGridBookingFields)
    : normalisedGridBookingFields;
  const bookingFieldIds = new Set(bookingFields.map((field) => field.id));
  const hasBookingField = (fieldId) => bookingFieldIds.has(fieldId);
  const enabledItemForms = normaliseItemForms(link.itemForms || []).filter((form) => (
    form.enabled !== false &&
    (!form.gridId || form.gridId === activePublicGrid?.id)
  ));
  const canUseInternalItemForms = Boolean(
    enabledItemForms.length &&
    (canUseInternalLinkTools || isDemoMode),
  );
  const enabledAssetFields = {
    sizes: false,
    colors: false,
    ...(link.assetFields || {}),
  };
  const accessRecord = (() => {
    if (!accessRecordStorageKey || typeof window === 'undefined') return null;
    try {
      const value = window.sessionStorage.getItem(accessRecordStorageKey);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      return null;
    }
  })();
  const canAddRegisterUpdates = Boolean(
    isRegisterGrid &&
    enabledAssetFields.updates &&
    (
      isDemoMode ||
      (user?.uid && (
      isPublicLink ||
      isSignedInLinkOwner ||
      hasCredentialAccess ||
      canUseInternalLinkTools
      ))
    ),
  );
  const canViewRegisterUpdates = Boolean(
    hasRegisterGrid &&
    enabledAssetFields.updates &&
    (
      isPublicLink ||
      isSignedInLinkOwner ||
      hasCredentialAccess ||
      canUseInternalLinkTools
    ),
  );
  const activeItemForm = enabledItemForms.find((form) => form.id === activeItemFormId) || enabledItemForms[0];
  const checkout = { ...defaultCheckout, ...(link.checkout || {}) };
  const checkoutBelongsToActiveGrid = activeGridUsesCheckout || !checkout.gridId || checkout.gridId === activePublicGrid?.id;
  const isRestaurantCheckout = Boolean(checkout.enableRestaurantOrdering);
  const canUsePosCheckout = Boolean(
    activeGridUsesCheckout &&
    checkoutBelongsToActiveGrid &&
    checkout.enablePosMode &&
    canUseInternalLinkTools
  );
  const canUseOnlineCheckout = checkout.enableOnlineCheckout !== false;
  const canUseCheckout = canUseOnlineCheckout || canUsePosCheckout;
  const isPosCheckout = canUsePosCheckout && (!canUseOnlineCheckout || checkoutMode === 'pos');
  const orderType = isRestaurantCheckout && checkoutMode === 'onsite' ? 'onsite' : 'offsite';
  const isOnsiteOrder = isRestaurantCheckout && !isPosCheckout && orderType === 'onsite';
  const isOffsiteOrder = isRestaurantCheckout && !isPosCheckout && orderType === 'offsite';
  const isStandardOnlineCheckout = !isPosCheckout && !isRestaurantCheckout;
  const deliveryFee = isOffsiteOrder ? Math.max(Number(checkout.deliveryFee) || 0, 0) : 0;
  const checkoutDetailsLabel = isPosCheckout
    ? 'Payment details'
    : isOnsiteOrder
      ? 'Table details'
      : checkout.customerDetailsLabel?.trim() || 'Customer details';
  const checkoutSubmitLabel = isPosCheckout
    ? 'Record sale'
    : checkout.submitLabel?.trim() || 'Send order';
  const showHeaderTitle = headerLogoIsDefault || isSquareHeaderLogo === true;
  const showHeroSection = link.showHeroSection !== false;
  const showCreateLinkButton = link.showCreateLinkButton !== false;
  const priceLabel = ['product', 'service'].includes(link.category) && link.price
    ? formatPrice(link.currency || 'ZAR', link.price)
    : '';
  const recipientPermissions = {
    canDownload: Boolean(link.allowDownloads),
    canShare: isPublicLink || Boolean(link.allowSharing),
  };
  const hasCheckoutGrid = checkoutGridCategories.includes(link.category) ||
    publicAssetGrids.some((grid) => checkoutGridCategories.includes(grid.category));
  const showHeaderCartButton = canUseCheckout && checkoutBelongsToActiveGrid && (activeGridUsesCheckout || (!activePublicGrid && hasCheckoutGrid));
  const showHeaderBookingButton = isAccommodationBookingForm;
  const areaOptions = [...new Set(activePublicGridAssets
    .map((asset) => String(asset.area || '').trim())
    .filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));
  const bookingVisibleAssets = isAccommodationBookingForm && bookingMode === 'booking' && bookingFilter.area
    ? gridAccessibleAssets.filter((asset) => String(asset.area || '').trim() === bookingFilter.area)
    : gridAccessibleAssets;
  const cartItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const cartCurrency = cartItems[0]?.currency || link.currency || 'ZAR';
  const cartTotal = cartItems.reduce((sum, item) => sum + item.lineTotal, 0);
  const cartTotalCost = cartItems.reduce((sum, item) => sum + (Number(item.lineCost) || 0), 0);
  const cartTotalProfit = cartItems.reduce((sum, item) => sum + (Number(item.lineProfit) || 0), 0);
  const checkoutTotal = cartTotal + deliveryFee;
  const bookingItemCount = isAccommodationBookingCategory
    ? bookingItems.reduce((sum, item) => sum + item.rooms, 0)
    : bookingItems.length;
  const today = todayDateValue();
  const canBookSelectedPlace = Boolean(
    !isAccommodationBookingForm ||
    (
      isAccommodationBookingCategory
        ? (!hasBookingField(CHECK_IN_FIELD_ID) || bookingFilter.checkInDate >= today) &&
          (!hasBookingField(CHECK_OUT_FIELD_ID) || bookingFilter.checkOutDate) &&
          (!hasBookingField(CHECK_IN_FIELD_ID) || !hasBookingField(CHECK_OUT_FIELD_ID) || bookingFilter.checkOutDate > bookingFilter.checkInDate)
        : isDigitalServiceBookingCategory ||
          (
            (!hasBookingField(SERVICE_DATE_FIELD_ID) || (bookingFilter.serviceDate && bookingFilter.serviceDate >= today)) &&
            (!hasBookingField(SERVICE_CATEGORY_FIELD_ID) || bookingFilter.serviceCategory) &&
            (!hasBookingField(SERVICE_TIME_FIELD_ID) || bookingFilter.serviceTime)
          )
    ),
  );
  const itemFormItemCount = itemFormItems.length;
  const itemFormOrderTotal = itemFormItems.reduce((sum, item) => sum + parsePrice(item.fields?.itemTotal), 0);
  const allRegisterUpdateCount = allRegisterUpdates.length;
  const publicPageWidthClass = publicPageWidthClasses[link.publicPageWidth] || publicPageWidthClasses['6xl'];
  const publicBackgroundColor = isValidHexColor(link.backgroundColor)
    ? link.backgroundColor.trim()
    : '#f7f9fb';
  const headerBackgroundColor = isValidHexColor(link.headerBackgroundColor)
    ? link.headerBackgroundColor.trim()
    : '#ffffff';
  const publicPageStyle = {
    backgroundColor: publicBackgroundColor,
    fontFamily: renderFontFamily(link.renderFont),
  };
  const publicHeaderStyle = {
    backgroundColor: headerBackgroundColor,
  };
  const contactTel = normalisePhoneNumber(link.contactTel);
  const contactWhatsApp = phoneNumberForWhatsApp(link.contactWhatsApp || link.contactTel);
  const contactEmail = String(link.contactEmail || '').trim();
  const hasContactActions = Boolean(contactTel || contactWhatsApp || contactEmail);

  if (isExpired) {
    return (
      <main className="grid min-h-screen place-items-center bg-[#f7f9fb] px-5 text-slate-950 antialiased">
        <section className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 text-center">
          <p className="label-caps">Shared asset link</p>
          <h1 className="mt-2 text-2xl font-semibold tracking-normal">Link expired</h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            This share link expired on {formatDateTime(link.endsAt)}.
          </p>
        </section>
      </main>
    );
  }

  const handleVerifyAccess = async (event) => {
    event.preventDefault();
    setAccessError('');
    setIsCheckingAccess(true);

    try {
      const accessRecord = await shareService.verifyShareAccessRecord(
        link.id,
        accessDraft.email,
        accessDraft.password,
      );

      if (!accessRecord?.accessToken) {
        setAccessError('Email address or password is incorrect.');
        return;
      }

      window.sessionStorage.setItem(accessStorageKey, 'granted');
      window.sessionStorage.setItem(accessRecordStorageKey, JSON.stringify({
        id: accessRecord.id,
        recipientEmail: accessRecord.recipientEmail || '',
        accessToken: accessRecord.accessToken || '',
      }));
      setHasCredentialAccess(true);
      await onAccessGranted?.(link.slug);
    } catch (error) {
      setAccessError(error.message || 'Unable to verify access.');
    } finally {
      setIsCheckingAccess(false);
    }
  };

  const handleInviteRequestSubmit = async (event) => {
    event.preventDefault();
    setInviteRequestStatus('');
    setInviteRequestError('');

    if (!inviteRequestDraft.email.trim() && !inviteRequestDraft.phone.trim()) {
      setInviteRequestError('Enter an email address or mobile number.');
      return;
    }

    setIsSubmittingInviteRequest(true);
    try {
      await shareService.createInviteRequest(link, inviteRequestDraft);
      setInviteRequestDraft({ email: '', phone: '', reason: '' });
      setInviteRequestStatus(inviteEntrySuccessMessage);
    } catch (error) {
      setInviteRequestError(error.message || 'Unable to send this invite request.');
    } finally {
      setIsSubmittingInviteRequest(false);
    }
  };

  const handleShareLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share(buildClientShareData(link, shareUrl));
        linkAnalyticsService.trackUniqueShare(link.id).catch((error) => {
          console.warn('Unable to track share action.', error);
        });
        return;
      }

      await navigator.clipboard.writeText(shareUrl);
      linkAnalyticsService.trackUniqueShare(link.id).catch((error) => {
        console.warn('Unable to track share action.', error);
      });
    } catch (error) {
      // Sharing is a best-effort browser capability; failed or cancelled shares do not block the page.
    }
  };

  const handleListInteraction = ({ grid, interactions }) => (
    interactionService.submitInteractions(link, grid, interactions)
  );

  const handleCreateLinkClick = () => {
    const baseUrl = new URL('/', DEFAULT_SHARE_ORIGIN);

    if (window.location.origin === baseUrl.origin) {
      window.history.pushState({}, '', baseUrl.pathname);
      onCreateLink?.();
      return;
    }

    window.location.assign(baseUrl.toString());
  };

  const closeResponseForm = () => setIsResponseFormOpen(false);

  const openResponseAction = () => {
    setIsCartOpen(false);
    setIsItemFormOpen(false);
    setIsInquiryOpen(false);
    if (isAccommodationBookingForm) {
      setBookingMode('request');
      setIsResponseFormOpen(false);
      setIsBookingOpen(true);
      return;
    }
    setIsBookingOpen(false);
    setIsResponseFormOpen(true);
  };

  const formatMoney = (currency, amount) => {
    if (!amount) return `${currencySymbol(currency)}0.00`;
    return `${currencySymbol(currency)}${amount.toFixed(2)}`;
  };

  const buildOrderItem = (asset, quantity, selectedOptions = {}) => {
    const isComboItem = Array.isArray(asset.comboProductIds) && asset.comboProductIds.length > 0;
    const hasStoreBasePrice = Boolean(String(asset.storeBasePrice || '').trim());
    const price = isComboItem || hasStoreBasePrice ? parsePrice(asset.price) : getEffectivePrice(asset);
    const unitCost = parsePrice(asset.unitCost);
    const unitProfit = parsePrice(asset.profit);
    const availableSizes = enabledAssetFields.sizes ? normaliseOptionList(asset.availableSizes) : [];
    const availableColors = enabledAssetFields.colors ? normaliseOptionList(asset.availableColors) : [];

    return {
      assetId: asset.id,
      title: asset.title || asset.name || 'Untitled item',
      imageUrl: imageUrlForAsset(asset),
      currency: asset.currency || link.currency || 'ZAR',
      price: price ? price.toFixed(2) : '',
      originalPrice: asset.price || '',
      unitCost: unitCost ? unitCost.toFixed(2) : '',
      profit: unitProfit ? unitProfit.toFixed(2) : '',
      quantity,
      lineTotal: price * quantity,
      lineCost: unitCost * quantity,
      lineProfit: unitProfit * quantity,
      availableSizes,
      availableColors,
      selectedSize: availableSizes.includes(selectedOptions.selectedSize) ? selectedOptions.selectedSize : '',
      selectedColor: availableColors.includes(selectedOptions.selectedColor) ? selectedOptions.selectedColor : '',
    };
  };

  const buildBookingItem = (asset, rooms = 1) => ({
    assetId: asset.id,
    title: asset.title || asset.name || 'Untitled accommodation',
    imageUrl: asset.preview || asset.downloadUrl || asset.iconUrl || '',
    availableRooms: quantityForAsset(asset),
    area: String(asset.area || '').trim(),
    rooms,
    beds: Math.max(Number(bookingFilter.beds) || 0, 0),
  });

  const bookingFilterAnswers = (nextFilter = bookingFilter) => {
    const answers = {
      [AREA_FIELD_ID]: nextFilter.area,
      ...(isAccommodationBookingCategory ? {
        [CHECK_IN_FIELD_ID]: nextFilter.checkInDate,
        [CHECK_OUT_FIELD_ID]: nextFilter.checkOutDate,
        [ADULT_COUNT_FIELD_ID]: nextFilter.adults,
        [KIDS_COUNT_FIELD_ID]: nextFilter.kids,
        [ROOMS_FIELD_ID]: nextFilter.rooms,
        [BEDS_FIELD_ID]: nextFilter.beds,
      } : {
        [SERVICE_CATEGORY_FIELD_ID]: nextFilter.serviceCategory,
        [SERVICE_TYPE_FIELD_ID]: nextFilter.serviceType,
        [SERVICE_DATE_FIELD_ID]: nextFilter.serviceDate,
        [SERVICE_TIME_FIELD_ID]: nextFilter.serviceTime,
        [SERVICE_ADDRESS_FIELD_ID]: nextFilter.serviceCategory === 'Transport' ? '' : nextFilter.serviceAddress,
        [PICKUP_ADDRESS_FIELD_ID]: nextFilter.serviceCategory === 'Transport' ? nextFilter.pickupAddress : '',
        [DROPOFF_ADDRESS_FIELD_ID]: nextFilter.serviceCategory === 'Transport' ? nextFilter.dropoffAddress : '',
        ...(nextFilter.serviceCategory === 'Transport' ? {
          [PICKUP_ADDRESS_FIELD_ID]: nextFilter.pickupAddress,
          [DROPOFF_ADDRESS_FIELD_ID]: nextFilter.dropoffAddress,
        } : {
          [SERVICE_ADDRESS_FIELD_ID]: nextFilter.serviceAddress,
        }),
      }),
    };

    return Object.fromEntries(
      Object.entries(answers).filter(([fieldId]) => hasBookingField(fieldId)),
    );
  };

  const updateBookingFilter = (field, value) => {
    setBookingFilter((currentFilter) => {
      const nextFilter = { ...currentFilter, [field]: value };

      if (field === 'checkInDate' && value) {
        nextFilter.checkOutDate = addDaysToDateValue(value, 1);
      }
      if (field === 'serviceCategory') {
        nextFilter.serviceType = '';
        nextFilter.serviceAddress = '';
        nextFilter.pickupAddress = '';
        nextFilter.dropoffAddress = '';
      }

      setBookingAnswers((currentAnswers) => ({
        ...currentAnswers,
        ...bookingFilterAnswers(nextFilter),
      }));

      return nextFilter;
    });
  };

  const openBookingRequest = () => {
    setBookingMode('request');
    setBookingItems([]);
    setBookingStatus('');
    setBookingError('');
    setBookingAnswers((currentAnswers) => ({
      ...currentAnswers,
      ...bookingFilterAnswers(),
      bookingType: 'Request',
    }));
    setIsCartOpen(false);
    setIsItemFormOpen(false);
    setIsResponseFormOpen(false);
    setIsInquiryOpen(false);
    setIsBookingOpen(true);
  };

  const handleAddToBooking = (asset) => {
    if (!isAccommodationBookingForm) return;
    const requestedRooms = Math.max(Number(bookingFilter.rooms) || 1, 1);
    const requestedBeds = Math.max(Number(bookingFilter.beds) || 0, 0);
    setBookingMode('booking');
    setBookingStatus('');
    setBookingError('');
    setIsCartOpen(false);
    setIsItemFormOpen(false);
    setIsResponseFormOpen(false);
    setIsInquiryOpen(false);
    setBookingAnswers((currentAnswers) => ({
      ...currentAnswers,
      ...bookingFilterAnswers(),
      bookingType: 'Booking',
    }));
    setIsBookingOpen(true);
    setBookingItems((currentItems) => {
      const existingItem = currentItems.find((item) => item.assetId === asset.id);
      if (existingItem) {
        return currentItems.map((item) => (
          item.assetId === asset.id
            ? {
              ...item,
              rooms: item.rooms + requestedRooms,
              beds: (Number(item.beds) || 0) + requestedBeds,
              availableRooms: quantityForAsset(asset),
            }
            : item
        ));
      }

      return [...currentItems, buildBookingItem(asset, requestedRooms)];
    });
    setIsBookingOpen(true);
  };

  const updateBookingRooms = (assetId, rooms) => {
    const nextRooms = Math.max(Number(rooms) || 0, 0);

    setBookingItems((currentItems) => (
      currentItems
        .map((item) => (item.assetId === assetId ? { ...item, rooms: nextRooms } : item))
        .filter((item) => item.rooms > 0)
    ));
  };

  const updateBookingItemBeds = (assetId, beds) => {
    const nextBeds = Math.max(Number(beds) || 0, 0);

    setBookingItems((currentItems) => (
      currentItems.map((item) => (item.assetId === assetId ? { ...item, beds: nextBeds } : item))
    ));
  };

  const updateBookingAnswer = (fieldId, value) => {
    setBookingAnswers((currentAnswers) => {
    const nextAnswers = { ...currentAnswers, [fieldId]: value };
      if (isAccommodationBookingCategory && fieldId === CHECK_IN_FIELD_ID && value) {
        nextAnswers[CHECK_OUT_FIELD_ID] = addDaysToDateValue(value, 1);
      }
      return nextAnswers;
    });
  };

  const validateBooking = () => {
    if (bookingMode === 'booking' && !bookingItems.length) {
      return isAccommodationBookingCategory ? 'Add at least one accommodation option.' : 'Add at least one service option.';
    }
    if (hasBookingField(AREA_FIELD_ID) && !bookingFilter.area.trim()) return 'Choose an area.';

    if (isAccommodationBookingCategory) {
      if (hasBookingField(CHECK_IN_FIELD_ID) && !bookingFilter.checkInDate) return 'Choose a check-in date.';
      if (hasBookingField(CHECK_OUT_FIELD_ID) && !bookingFilter.checkOutDate) return 'Choose a check-out date.';
      if (hasBookingField(CHECK_IN_FIELD_ID) && bookingFilter.checkInDate && bookingFilter.checkInDate < today) return 'Check-in date cannot be in the past.';
      if (hasBookingField(CHECK_OUT_FIELD_ID) && bookingFilter.checkOutDate && bookingFilter.checkOutDate < today) return 'Check-out date cannot be in the past.';
      if (hasBookingField(CHECK_IN_FIELD_ID) && hasBookingField(CHECK_OUT_FIELD_ID) && bookingFilter.checkInDate && bookingFilter.checkOutDate && bookingFilter.checkOutDate <= bookingFilter.checkInDate) {
        return 'Check-out date must be after check-in date.';
      }
      const totalGuests = (Number(bookingFilter.adults) || 0) + (Number(bookingFilter.kids) || 0);
      if ((hasBookingField(ADULT_COUNT_FIELD_ID) || hasBookingField(KIDS_COUNT_FIELD_ID)) && totalGuests < 1) return 'Enter at least one adult or kid.';
      if (hasBookingField(ROOMS_FIELD_ID) && Number(bookingFilter.rooms) < 1) return 'Enter at least one room.';
      if (hasBookingField(BEDS_FIELD_ID) && Number(bookingFilter.beds) < 0) return 'Beds cannot be negative.';
    } else {
      if (hasBookingField(SERVICE_CATEGORY_FIELD_ID) && !bookingFilter.serviceCategory.trim()) return 'Choose a service category.';
      if (hasBookingField(SERVICE_TYPE_FIELD_ID) && !bookingFilter.serviceType.trim()) return 'Choose a service type.';
      if (hasBookingField(SERVICE_DATE_FIELD_ID) && !bookingFilter.serviceDate) return 'Choose a service date.';
      if (hasBookingField(SERVICE_DATE_FIELD_ID) && bookingFilter.serviceDate && bookingFilter.serviceDate < today) return 'Service date cannot be in the past.';
      if (hasBookingField(SERVICE_TIME_FIELD_ID) && !bookingFilter.serviceTime) return 'Choose a service time.';
      if (isTransportServiceRequest) {
        if (hasBookingField(PICKUP_ADDRESS_FIELD_ID) && !bookingFilter.pickupAddress.trim()) return 'Enter a pickup address.';
        if (hasBookingField(DROPOFF_ADDRESS_FIELD_ID) && !bookingFilter.dropoffAddress.trim()) return 'Enter a drop-off address.';
      } else if (hasBookingField(SERVICE_ADDRESS_FIELD_ID) && !bookingFilter.serviceAddress.trim()) {
        return 'Enter the service address.';
      }
    }

    const unavailableItem = isAccommodationBookingCategory
      ? bookingItems.find((item) => item.rooms > item.availableRooms)
      : null;
    if (unavailableItem) {
      return `${unavailableItem.title} only has ${unavailableItem.availableRooms} room${unavailableItem.availableRooms === 1 ? '' : 's'} available.`;
    }

    const missingField = bookingFields.find((field) => {
      if (!field.required) return false;
      const value = bookingAnswers[field.id];
      if (field.type === 'checkbox') return !value;
      if (field.type === 'multiselect') return !Array.isArray(value) || !value.length;
      return !String(value || '').trim();
    });

    if (missingField) return `${missingField.label} is required.`;

    const checkInDate = bookingAnswers[CHECK_IN_FIELD_ID];
    const checkOutDate = bookingAnswers[CHECK_OUT_FIELD_ID];
    if (hasBookingField(CHECK_IN_FIELD_ID) && hasBookingField(CHECK_OUT_FIELD_ID) && checkInDate && checkOutDate && checkOutDate <= checkInDate) {
      return 'Check-out date must be after check-in date.';
    }

    return '';
  };

  const handleBookingSubmit = async (event) => {
    event.preventDefault();
    setBookingStatus('');
    setBookingError('');

    const validationError = validateBooking();
    if (validationError) {
      setBookingError(validationError);
      return;
    }

    setIsSubmittingBooking(true);
    try {
      const bookingSummary = bookingItems
        .map((item) => (
          isAccommodationBookingCategory
            ? `${item.title}: ${item.rooms} room${item.rooms === 1 ? '' : 's'}, ${item.beds || 0} bed${Number(item.beds) === 1 ? '' : 's'} requested (${item.availableRooms} available)`
            : `${item.title}: service requested`
        ))
        .join('\n');

      await formService.submitResponse(link, {
        ...bookingAnswers,
        ...bookingFilterAnswers(),
        bookingType: bookingMode === 'request' ? 'Request' : 'Booking',
        bookingItems: bookingSummary,
        bookingItemDetails: bookingItems,
      });
      setBookingItems([]);
      setBookingAnswers({});
      setBookingStatus(bookingMode === 'request' ? 'Request submitted.' : 'Booking submitted.');
    } catch (error) {
      setBookingError(error.message || 'Unable to submit this accommodation request.');
    } finally {
      setIsSubmittingBooking(false);
    }
  };

  const renderBookingInput = (field) => {
    const value = bookingAnswers[field.id] ?? (field.type === 'checkbox' ? false : '');
    const inputId = `booking-${field.id}`;

    if (field.type === 'textarea') {
      return (
        <textarea
          className="field min-h-24 resize-none"
          id={inputId}
          onChange={(event) => updateBookingAnswer(field.id, event.target.value)}
          placeholder={field.placeholder || ''}
          value={value}
        />
      );
    }

    if (field.type === 'checkbox') {
      return (
        <label className="flex items-start gap-3 rounded border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-700">
          <input
            checked={Boolean(value)}
            id={inputId}
            onChange={(event) => updateBookingAnswer(field.id, event.target.checked)}
            type="checkbox"
          />
          <span>{field.placeholder || field.label}</span>
        </label>
      );
    }

    if (field.type === 'select') {
      return (
        <select
          className="field"
          id={inputId}
          onChange={(event) => updateBookingAnswer(field.id, event.target.value)}
          value={value}
        >
          <option value="">Select an option</option>
          {normaliseOptionList(field.options || []).map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (field.type === 'multiselect') {
      const selectedValues = Array.isArray(value) ? value : [];
      return (
        <select
          className="field min-h-32"
          id={inputId}
          multiple
          onChange={(event) => updateBookingAnswer(
            field.id,
            Array.from(event.target.selectedOptions).map((option) => option.value),
          )}
          value={selectedValues}
        >
          {normaliseOptionList(field.options || []).map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    return (
      <input
        className="field"
        id={inputId}
        inputMode={field.type === 'tel' ? 'tel' : field.type === 'number' ? 'decimal' : undefined}
        min={field.id === CHECK_OUT_FIELD_ID ? addDaysToDateValue(bookingAnswers[CHECK_IN_FIELD_ID], 1) || undefined : undefined}
        onChange={(event) => updateBookingAnswer(field.id, event.target.value)}
        placeholder={field.placeholder || ''}
        type={field.type === 'date' ? 'date' : field.type === 'time' ? 'time' : field.type === 'email' ? 'email' : field.type === 'tel' ? 'tel' : field.type === 'number' ? 'number' : 'text'}
        value={value}
      />
    );
  };

  const renderBookingField = (field) => (
    <div key={field.id}>
      {field.type !== 'checkbox' && (
        <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor={`booking-${field.id}`}>
          {field.label}
          {field.required && <span className="text-red-600"> *</span>}
        </label>
      )}
      {renderBookingInput(field)}
    </div>
  );

  const renderBookingFields = () => {
    const fields = (isAccommodationBookingForm ? bookingFields : gridScopedForm?.fields || [])
      .filter((field) => !BOOKING_FILTER_FIELD_IDS.has(field.id));
    const fieldById = new Map(fields.map((field) => [field.id, field]));
    const renderedFieldIds = new Set();
    const fieldPairs = [
      ['guest-email', 'guest-phone'],
      [CHECK_IN_FIELD_ID, CHECK_OUT_FIELD_ID],
      ['adult-count', 'kids-count'],
    ];

    const pairedRows = fieldPairs
      .map((pair) => pair.map((fieldId) => fieldById.get(fieldId)).filter(Boolean))
      .filter((pairFields) => pairFields.length);

    pairedRows.forEach((pairFields) => {
      pairFields.forEach((field) => renderedFieldIds.add(field.id));
    });

    return (
      <>
        {fields.filter((field) => !renderedFieldIds.has(field.id)).map(renderBookingField)}
        {pairedRows.map((pairFields) => (
          <div className="grid gap-4 grid-cols-2" key={pairFields.map((field) => field.id).join('-')}>
            {pairFields.map(renderBookingField)}
          </div>
        ))}
      </>
    );
  };

  const itemTitle = (asset = {}) => asset.title || asset.name || asset.description || 'Untitled item';

  const defaultItemFieldValue = (field) => (field.type === 'checkbox' ? false : '');

  const quantityForAsset = (asset = {}) => {
    const quantity = Number(asset.quantity);
    return Number.isFinite(quantity) ? quantity : 0;
  };

  const isStockTakeForm = (form = {}) => form.preset === 'stock-taking';
  const isOrderingForm = (form = {}) => form.preset === 'ordering';
  const isStockTakeCountField = (field = {}) => (
    isStockTakeForm(activeItemForm) && ['countedQuantity', 'quantity'].includes(field.id)
  );
  const orderingItemTotal = (fields = {}) => parsePrice(fields.unitCost) * Math.max(Number(fields.orderedQuantity) || 0, 0);
  const withOrderingTotals = (fields = {}) => (
    isOrderingForm(activeItemForm)
      ? {
        ...fields,
        itemTotal: orderingItemTotal(fields).toFixed(2),
      }
      : fields
  );

  const buildItemFormEntry = (asset) => ({
    itemId: asset.id,
    itemTitle: itemTitle(asset),
    imageUrl: imageUrlForAsset(asset),
    fields: withOrderingTotals({
      ...Object.fromEntries((activeItemForm?.itemFields || []).map((field) => [
        field.id,
        field.id === 'unitCost' ? parsePrice(asset.unitCost).toFixed(2) : defaultItemFieldValue(field),
      ])),
      ...(isStockTakeForm(activeItemForm) ? {
        storedQuantity: quantityForAsset(asset),
        countedQuantity: '',
        quantityDifference: '',
      } : {}),
      ...(isOrderingForm(activeItemForm) ? {
        orderedQuantity: '',
        unitCost: parsePrice(asset.unitCost).toFixed(2),
      } : {}),
    }),
  });

  const handleAddToItemForm = (asset) => {
    if (!canUseInternalItemForms || !activeItemForm) return;
    setItemFormStatus('');
    setItemFormError('');
    setIsCartOpen(false);
    setIsResponseFormOpen(false);
    setIsBookingOpen(false);
    setIsRegisterUpdateOpen(false);
    if (!itemFormItems.length) setItemFormOrderDraft(defaultItemFormOrderDraft());
    setItemFormItems((currentItems) => (
      currentItems.some((item) => item.itemId === asset.id)
        ? currentItems
        : [...currentItems, buildItemFormEntry(asset)]
    ));
    setIsItemFormOpen(true);
  };

  const assetForRegisterUpdate = (update = {}) => (
    visibleAssets.find((asset) => asset.id === update.assetId) || {
      id: update.assetId,
      title: update.assetTitle,
    }
  );

  const registerUpdateAssetImage = (update = {}) => imageUrlForAsset(assetForRegisterUpdate(update));

  const renderRegisterUpdateRecord = (update) => {
    const updateAsset = assetForRegisterUpdate(update);
    const itemImageUrl = registerUpdateAssetImage(update);
    const itemName = updateAsset.title || updateAsset.name || update.assetTitle || 'Register item';
    const statusOption = registerStatusOptionFor(update.status);

    return (
      <article className="grid grid-cols-[3.5rem_1fr] gap-3 rounded border border-slate-200 bg-slate-50 p-3" key={update.id}>
        <div className="min-w-0">
          <div className="grid h-14 w-14 place-items-center overflow-hidden rounded border border-slate-200 bg-white text-xs font-semibold uppercase text-slate-400">
            {itemImageUrl ? (
              <img
                alt={`${itemName} preview`}
                className="h-full w-full object-cover"
                src={itemImageUrl}
              />
            ) : (
              <span>{String(itemName).slice(0, 2)}</span>
            )}
          </div>
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="min-w-0 truncate text-sm font-semibold text-slate-950">{itemName}</p>
            {update.updateType && <span className="chip">{update.updateType}</span>}
            {statusOption ? (
              <span className={`inline-grid h-6 min-w-6 place-items-center rounded-full px-2 text-xs font-bold ${statusOption.className}`}>
                {statusOption.label}
              </span>
            ) : update.status ? (
              <span className="chip">{update.status}</span>
            ) : null}
            {update.quantity && <span className="chip">Qty: {update.quantity}</span>}
          </div>
          <span className="mt-1 block text-xs font-semibold text-slate-500">
            {formatDateTime(update.dateTime || update.createdAt)}
          </span>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">{update.message}</p>
          <p className="mt-2 text-xs font-semibold text-slate-500">
            {update.authorName || update.authorEmail || 'Signed-in user'}
          </p>
        </div>
      </article>
    );
  };

  const loadRegisterUpdates = async (asset) => {
    if (!link?.id || !asset?.id) return;
    setIsLoadingRegisterUpdates(true);
    setRegisterUpdateError('');
    try {
      const updates = await registerUpdateService.listUpdates(link.id, asset.id);
      setRegisterUpdates(updates);
    } catch (error) {
      setRegisterUpdateError(error.message || 'Unable to load updates for this register item.');
    } finally {
      setIsLoadingRegisterUpdates(false);
    }
  };

  const loadAllRegisterUpdates = async () => {
    if (!link?.id) return;
    setIsLoadingAllRegisterUpdates(true);
    setRegisterUpdateError('');
    try {
      const updates = await registerUpdateService.listUpdatesForLink(link.id);
      setAllRegisterUpdates(updates);
    } catch (error) {
      setRegisterUpdateError(error.message || 'Unable to load register updates.');
    } finally {
      setIsLoadingAllRegisterUpdates(false);
    }
  };

  const handleOpenAllRegisterUpdates = () => {
    if (!canViewRegisterUpdates) return;
    setRegisterUpdateStatus('');
    setRegisterUpdateError('');
    setIsCartOpen(false);
    setIsResponseFormOpen(false);
    setIsBookingOpen(false);
    setIsItemFormOpen(false);
    setIsInquiryOpen(false);
    setIsRegisterUpdateOpen(false);
    setIsAllRegisterUpdatesOpen(true);
    loadAllRegisterUpdates();
  };

  const handleOpenRegisterUpdate = (asset) => {
    if (!canAddRegisterUpdates) return;
    setActiveRegisterAsset(asset);
    setRegisterUpdateDraft({
      ...defaultRegisterUpdateDraft(),
      status: registerStatusOptionFor(asset.status)?.value || '',
    });
    setRegisterUpdateStatus('');
    setRegisterUpdateError('');
    setRegisterUpdates([]);
    setIsCartOpen(false);
    setIsResponseFormOpen(false);
    setIsBookingOpen(false);
    setIsItemFormOpen(false);
    setIsInquiryOpen(false);
    setIsRegisterUpdateOpen(true);
    loadRegisterUpdates(asset);
  };

  const handleRegisterUpdateSubmit = async (event) => {
    event.preventDefault();
    setRegisterUpdateStatus('');
    setRegisterUpdateError('');

    if (isDemoMode) {
      setRegisterUpdateError('Submissions are disabled while this link is in demo mode.');
      return;
    }

    if (!activeRegisterAsset) {
      setRegisterUpdateError('Choose a register item before adding an update.');
      return;
    }

    setIsSubmittingRegisterUpdate(true);
    try {
      const created = await registerUpdateService.addUpdate(link, activeRegisterAsset, {
        ...registerUpdateDraft,
        accessShareId: accessRecord?.id || '',
      });
      setRegisterUpdates((currentUpdates) => [created, ...currentUpdates]);
      setAllRegisterUpdates((currentUpdates) => [created, ...currentUpdates]);
      setRegisterUpdateDraft({
        ...defaultRegisterUpdateDraft(),
        updateType: registerUpdateDraft.updateType || 'Record',
        status: registerUpdateDraft.status || '',
      });
      setRegisterUpdateStatus('Update added.');
    } catch (error) {
      setRegisterUpdateError(error.message || 'Unable to add this register update.');
    } finally {
      setIsSubmittingRegisterUpdate(false);
    }
  };

  const handleOpenInquiry = (asset) => {
    setActiveInquiryAsset(asset);
    setInquiryStatus('');
    setInquiryError('');
    setInquiryAnswers({
      name: '',
      email: '',
      phone: '',
      message: `Hi, I would like to enquire about ${itemTitle(asset)}.`,
    });
    setIsCartOpen(false);
    setIsResponseFormOpen(false);
    setIsBookingOpen(false);
    setIsItemFormOpen(false);
    setIsInquiryOpen(true);
  };

  const updateInquiryAnswer = (field, value) => {
    setInquiryAnswers((currentAnswers) => ({
      ...currentAnswers,
      [field]: value,
    }));
  };

  const validateInquiry = () => {
    if (!activeInquiryAsset) return 'Choose a listing before sending an enquiry.';
    if (!inquiryAnswers.name.trim()) return 'Name is required.';
    if (!inquiryAnswers.email.trim()) return 'Email is required.';
    if (!inquiryAnswers.phone.trim()) return 'Phone is required.';
    if (!inquiryAnswers.message.trim()) return 'Message is required.';
    return '';
  };

  const handleInquirySubmit = async (event) => {
    event.preventDefault();
    setInquiryStatus('');
    setInquiryError('');

    const validationError = validateInquiry();
    if (validationError) {
      setInquiryError(validationError);
      return;
    }

    setIsSubmittingInquiry(true);
    try {
      await formService.submitResponse(link, {
        inquiryType: 'Listing inquiry',
        listingId: activeInquiryAsset.id,
        listingTitle: itemTitle(activeInquiryAsset),
        listingPrice: activeInquiryAsset.price || '',
        listingArea: activeInquiryAsset.area || '',
        listingCategory: activeInquiryAsset.category || '',
        customerName: inquiryAnswers.name,
        customerEmail: inquiryAnswers.email,
        customerPhone: inquiryAnswers.phone,
        message: inquiryAnswers.message,
      });
      setInquiryStatus('Inquiry sent.');
      setInquiryAnswers({
        name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      setInquiryError(error.message || 'Unable to send this inquiry.');
    } finally {
      setIsSubmittingInquiry(false);
    }
  };

  const updateItemFormField = (itemId, fieldId, value) => {
    setItemFormItems((currentItems) => currentItems.map((item) => (
      item.itemId === itemId
        ? {
          ...item,
          fields: withOrderingTotals({
            ...item.fields,
            [fieldId]: value,
            ...(isStockTakeForm(activeItemForm) && ['countedQuantity', 'quantity'].includes(fieldId)
              ? { quantityDifference: Number(value || 0) - Number(item.fields?.storedQuantity || 0) }
              : {}),
          }),
        }
        : item
    )));
  };

  const removeItemFormItem = (itemId) => {
    setItemFormItems((currentItems) => currentItems.filter((item) => item.itemId !== itemId));
  };

  const updateItemFormOrderDraft = (field, value) => {
    setItemFormOrderDraft((currentDraft) => ({
      ...currentDraft,
      [field]: value,
    }));
  };

  const renderItemFormItemImage = (item) => (
    <div className="grid h-16 w-20 shrink-0 place-items-center overflow-hidden rounded border border-slate-200 bg-white text-xs font-semibold uppercase text-slate-400">
      {item.imageUrl ? (
        <img
          alt={`${item.itemTitle} preview`}
          className="h-full w-full object-cover"
          src={item.imageUrl}
        />
      ) : (
        <span>{String(item.itemTitle || 'Item').slice(0, 2)}</span>
      )}
    </div>
  );

  const validateItemForm = () => {
    if (!activeItemForm) return 'Choose an item form before submitting.';
    if (!itemFormItems.length) return 'Add at least one item to this form.';
    if (isOrderingForm(activeItemForm) && !itemFormOrderDraft.status.trim()) return 'Order status is required.';

    for (const item of itemFormItems) {
      const missingField = (activeItemForm.itemFields || []).find((field) => {
        if (!field.required) return false;
        const value = item.fields?.[field.id];
        if (field.type === 'checkbox') return !value;
        return !String(value || '').trim();
      });

      if (missingField) return `${missingField.label} is required for ${item.itemTitle}.`;
    }

    return '';
  };

  const handleItemFormSubmit = async (event) => {
    event.preventDefault();
    setItemFormStatus('');
    setItemFormError('');

    if (isDemoMode) {
      setItemFormError('Submissions are disabled while this link is in demo mode.');
      return;
    }

    const validationError = validateItemForm();
    if (validationError) {
      setItemFormError(validationError);
      return;
    }

    setIsSubmittingItemForm(true);
    try {
      await itemFormService.submitResponse(link, activeItemForm, itemFormItems, {
        orderFields: isOrderingForm(activeItemForm) ? itemFormOrderDraft : {},
      });
      setItemFormItems([]);
      setItemFormOrderDraft(defaultItemFormOrderDraft());
      setItemFormStatus('Item form submitted.');
    } catch (error) {
      setItemFormError(error.message || 'Unable to submit item form.');
    } finally {
      setIsSubmittingItemForm(false);
    }
  };

  const renderItemFormInput = (item, field) => {
    const value = item.fields?.[field.id] ?? defaultItemFieldValue(field);
    const inputId = `share-item-form-${item.itemId}-${field.id}`;

    if (field.type === 'textarea') {
      return (
        <textarea
          className="field min-h-24 resize-none"
          id={inputId}
          onChange={(event) => updateItemFormField(item.itemId, field.id, event.target.value)}
          placeholder={field.placeholder || ''}
          value={value}
        />
      );
    }

    if (isStockTakeCountField(field)) {
      const storedQuantity = Number(item.fields?.storedQuantity) || 0;
      const countedQuantity = value === '' ? '' : Number(value);
      const difference = countedQuantity === '' ? '' : countedQuantity - storedQuantity;

      return (
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded border border-slate-200 bg-white px-3 py-2">
            <p className="text-xs font-semibold uppercase text-slate-500">Stored</p>
            <p className="mt-1 text-sm font-semibold text-slate-950">{storedQuantity}</p>
          </div>
          <div>
            <label className="sr-only" htmlFor={inputId}>Counted</label>
            <p className="mb-1 text-xs font-semibold uppercase text-slate-500">Counted</p>
            <input
              className="field"
              id={inputId}
              inputMode="decimal"
              onChange={(event) => updateItemFormField(item.itemId, field.id, event.target.value)}
              placeholder={field.placeholder || ''}
              type="number"
              value={value}
            />
          </div>
          <div className="rounded border border-slate-200 bg-white px-3 py-2">
            <p className="text-xs font-semibold uppercase text-slate-500">Difference</p>
            <p className={`mt-1 text-sm font-semibold ${Number(difference) < 0 ? 'text-red-700' : Number(difference) > 0 ? 'text-emerald-700' : 'text-slate-950'}`}>
              {difference === '' ? '-' : difference}
            </p>
          </div>
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <select
          className="field"
          disabled={field.readOnly}
          id={inputId}
          onChange={(event) => updateItemFormField(item.itemId, field.id, event.target.value)}
          value={value}
        >
          <option value="">Select an option</option>
          {normaliseOptionList(field.options || []).map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <label className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
          <input
            checked={Boolean(value)}
            disabled={field.readOnly}
            id={inputId}
            onChange={(event) => updateItemFormField(item.itemId, field.id, event.target.checked)}
            type="checkbox"
          />
          {field.placeholder || field.label}
        </label>
      );
    }

    if (field.type === 'currency') {
      return (
        <div className="relative">
          <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center border-r border-slate-200 text-sm font-semibold text-slate-600">
            {field.currencySymbol || 'R'}
          </span>
          <input
            className={`field pl-12 ${field.readOnly ? 'bg-slate-100 text-slate-600' : ''}`}
            disabled={field.readOnly}
            id={inputId}
            inputMode="decimal"
            onChange={(event) => updateItemFormField(item.itemId, field.id, event.target.value)}
            placeholder={field.placeholder || '0.00'}
            step="any"
            type="number"
            value={value}
          />
        </div>
      );
    }

    return (
      <input
        className={`field ${field.readOnly ? 'bg-slate-100 text-slate-600' : ''}`}
        disabled={field.readOnly}
        id={inputId}
        inputMode={field.type === 'number' ? 'decimal' : undefined}
        onChange={(event) => updateItemFormField(item.itemId, field.id, event.target.value)}
        placeholder={field.placeholder || ''}
        type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
        value={value}
      />
    );
  };

  const handleAddToCart = (asset) => {
    setCheckoutStatus('');
    setCheckoutError('');
    setCheckoutPaymentDetails(null);
    setIsResponseFormOpen(false);
    setIsItemFormOpen(false);
    setIsBookingOpen(false);
    setIsInquiryOpen(false);
    setCartItems((currentItems) => {
      const existingItem = currentItems.find((item) => item.assetId === asset.id);
      if (existingItem) {
        return currentItems.map((item) => (
          item.assetId === asset.id ? buildOrderItem(asset, item.quantity + 1, item) : item
        ));
      }

      return [...currentItems, buildOrderItem(asset, 1)];
    });
  };

  const updateCartQuantity = (assetId, quantity) => {
    const nextQuantity = Math.max(Number(quantity) || 0, 0);

    setCartItems((currentItems) => (
      currentItems
        .map((item) => (item.assetId === assetId
          ? {
            ...item,
            quantity: nextQuantity,
            lineTotal: parsePrice(item.price) * nextQuantity,
            lineCost: parsePrice(item.unitCost) * nextQuantity,
            lineProfit: parsePrice(item.profit) * nextQuantity,
          }
          : item))
        .filter((item) => item.quantity > 0)
    ));
  };

  const updateCartOption = (assetId, field, value) => {
    setCartItems((currentItems) => currentItems.map((item) => (
      item.assetId === assetId ? { ...item, [field]: value } : item
    )));
  };

  const handleCheckout = async (event) => {
    event.preventDefault();
    setCheckoutStatus('');
    setCheckoutError('');
    setCheckoutPaymentDetails(null);

    if (isOnsiteOrder && !customerDraft.tableNumber.trim()) {
      setCheckoutError('Table number is required.');
      return;
    }

    if (isOffsiteOrder && (!customerDraft.name.trim() || !customerDraft.email.trim() || !customerDraft.deliveryAddress.trim())) {
      setCheckoutError('Name, email, and delivery address are required.');
      return;
    }

    if (isStandardOnlineCheckout && (!customerDraft.name.trim() || !customerDraft.email.trim())) {
      setCheckoutError('Name and email are required.');
      return;
    }

    const missingOptionItem = cartItems.find((item) => (
      (item.availableSizes?.length && !item.selectedSize) ||
      (item.availableColors?.length && !item.selectedColor)
    ));

    if (missingOptionItem) {
      setCheckoutError(`Choose all available options for ${missingOptionItem.title}.`);
      return;
    }

    setIsCheckingOut(true);
    try {
      const checkoutCustomer = isPosCheckout
        ? { name: 'Walk-in customer', email: '', phone: '', notes: '' }
        : isOnsiteOrder
          ? { name: `Table ${customerDraft.tableNumber.trim()}`, email: '', phone: '', notes: customerDraft.notes }
          : customerDraft;

      const submittedOrder = await orderService.submitOrder(link, checkoutCustomer, cartItems, {
        source: isPosCheckout ? 'pos' : 'online',
        paymentMethod: isPosCheckout ? customerDraft.paymentMethod : '',
        orderType: isRestaurantCheckout && !isPosCheckout ? orderType : '',
        tableNumber: isOnsiteOrder ? customerDraft.tableNumber : '',
        deliveryAddress: isOffsiteOrder ? customerDraft.deliveryAddress : '',
        deliveryFee,
        totalCost: cartTotalCost,
        totalProfit: cartTotalProfit,
      });
      setCartItems([]);
      setCustomerDraft({
        name: '',
        email: '',
        phone: '',
        notes: '',
        deliveryAddress: '',
        tableNumber: '',
        paymentMethod: 'Cash',
      });
      setCheckoutStatus(isPosCheckout ? 'Sale recorded.' : checkout.successMessage?.trim() || 'Order sent.');
      if (!isPosCheckout && checkout.enableOfflinePaymentDetails) {
        setCheckoutPaymentDetails({
          reference: submittedOrder.orderNumber || submittedOrder.id || '',
          amount: submittedOrder.total,
          currency: submittedOrder.currency || cartCurrency,
        });
      }
    } catch (error) {
      setCheckoutError(error.message || 'Unable to send this order.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  const renderShareHero = ({ showActions = true } = {}) => (
    <div className="mb-6 overflow-hidden rounded-lg border border-slate-200 bg-white">
      {link.ogImage && (
        <img className="h-auto w-full object-cover md:aspect-[16/7]" src={link.ogImage} alt="Open graph preview" />
      )}
      <div className="p-4">
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 text-md">
          {link.category && <span className="chip">{link.category}</span>}
          {priceLabel && <span className="chip">{priceLabel}</span>}
          {showActions && (recipientPermissions.canShare || hasResponseForm) && (
            <div className="flex flex-wrap gap-2">
              {recipientPermissions.canShare && (
                <button
                  className="rounded border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                  onClick={handleShareLink}
                  type="button"
                >
                  Share link
                </button>
              )}
              {hasResponseForm && !isAccommodationBookingForm && (
                <button
                  className="rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                  onClick={openResponseAction}
                  type="button"
                >
                  {responseFormButtonLabel}
                </button>
              )}
            </div>
          )}
          {showActions && hasContactActions && (
            <div className="flex flex-wrap gap-2">
              {contactTel && (
                <a
                  aria-label="Call"
                  className="public-contact-icon-button"
                  href={`tel:${contactTel}`}
                  title="Call"
                >
                  <PhoneIcon />
                </a>
              )}
              {contactWhatsApp && (
                <a
                  aria-label="Message on WhatsApp"
                  className="public-contact-icon-button"
                  href={`https://wa.me/${contactWhatsApp}`}
                  rel="noreferrer"
                  target="_blank"
                  title="WhatsApp"
                >
                  <MessageIcon />
                </a>
              )}
              {contactEmail && (
                <a
                  aria-label="Send email"
                  className="public-contact-icon-button"
                  href={`mailto:${contactEmail}`}
                  title="Email"
                >
                  <MailIcon />
                </a>
              )}
            </div>
          )}
        </div>
        {link.description && (
          <div className="asset-description mt-3 max-w-3xl text-sm leading-6 text-slate-600">
            <ReactMarkdown>{link.description}</ReactMarkdown>
          </div>
        )}
        <div className="mt-5 flex flex-wrap gap-2">
          <span className="chip">{link.assets?.length || 0} assets</span>
          {link.endsAt && <span className="chip">Expires: {formatDateTime(link.endsAt)}</span>}
        </div>
      </div>
    </div>
  );

  const renderPublicFooter = () => (
    <footer className={`mx-auto ${publicPageWidthClass} px-5 pb-8 pt-2 text-center sm:px-8`}>
      <div className="border-t border-slate-200 pt-6">
        <img
          alt={`${link.title || link.owner || 'Link'} logo`}
          className="public-share-footer-logo mx-auto h-12 max-w-40 object-contain"
          src={link.logo || defaultLinkIcon}
        />
        {link.description && (
          <div className="asset-description mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-500">
            <ReactMarkdown>{link.description}</ReactMarkdown>
          </div>
        )}
        <p className="mt-4 text-xs font-semibold text-slate-500">
          Powered by{' '}
          <a
            className="text-slate-950 underline decoration-slate-300 underline-offset-4 transition hover:decoration-slate-950"
            href="https://sharabl.co.za"
            rel="noreferrer"
            target="_blank"
          >
            Sharabl
          </a>
        </p>
      </div>
    </footer>
  );

  const gridSearchInput = (
    <label className="relative block w-full">
      <span className="sr-only">Search {activePublicGrid?.title || 'active grid'}</span>
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400"><SearchIcon /></span>
      <input
        className="field h-10 w-full pl-10"
        onChange={(event) => setActiveGridSearch(event.target.value)}
        placeholder={`Search ${activePublicGrid?.title || 'active grid'}`}
        type="search"
        value={activeGridSearch}
      />
    </label>
  );

  return (
    <main className="min-h-screen bg-[#f7f9fb] text-slate-950 antialiased" style={publicPageStyle}>
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white" style={publicHeaderStyle}>
        <div className="mx-auto grid max-w-6xl grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-2 py-2 sm:px-4 lg:grid-cols-[auto_minmax(12rem,1fr)_auto] lg:gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div
              className="flex w-auto items-center justify-center overflow-hidden bg-white"
              style={{ height: `${headerLogoHeight}px` }}
            >
              <img
                style={headerLogoImageStyle}
                src={headerLogo}
                alt={`${link.owner || 'Sharabl'} logo`}
                onLoad={(event) => {
                  const image = event.currentTarget;
                  setIsSquareHeaderLogo(Math.abs(image.naturalWidth - image.naturalHeight) <= 2);
                }}
              />
            </div>
            {showHeaderTitle && (
              <div>
                <p className="text-lg font-semibold leading-none text-slate-950">{link.title}</p>
                <p className="mt-1 break-all text-xs text-slate-500">{shareUrl}</p>
              </div>
            )}
          </div>

          <div className="hidden min-w-0 lg:block">{gridSearchInput}</div>

          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
            {isDemoMode && <span className="chip">Demo</span>}
            {!isPublicLink && <span className="chip">{link.protection}</span>}
            {canViewRegisterUpdates && (
              <button
                aria-label={`Open register updates with ${allRegisterUpdateCount} loaded updates`}
                className="relative grid h-10 w-10 place-items-center rounded border border-slate-300 bg-white text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                onClick={handleOpenAllRegisterUpdates}
                title="Updates"
                type="button"
              >
                <UpdatesIcon />
                {allRegisterUpdateCount > 0 && (
                  <span className="absolute -right-2 -top-2 grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-slate-950 px-1 text-[0.625rem] font-semibold leading-none text-white">
                    {allRegisterUpdateCount}
                  </span>
                )}
              </button>
            )}
            {canUseInternalItemForms && (
              <div className="flex items-center gap-2">
                {enabledItemForms.length > 1 && (
                  <select
                    aria-label="Internal item form"
                    className="field h-10 max-w-[11rem] py-2 text-sm"
                    onChange={(event) => {
                      setActiveItemFormId(event.target.value);
                      setItemFormItems([]);
                      setItemFormError('');
                      setItemFormStatus('');
                    }}
                    value={activeItemForm?.id || ''}
                  >
                    {enabledItemForms.map((form) => (
                      <option key={form.id} value={form.id}>{form.title}</option>
                    ))}
                  </select>
                )}
                <button
                  aria-label={`Open internal item form with ${itemFormItemCount} items`}
                  className="relative grid h-10 w-10 place-items-center rounded border border-slate-300 bg-white text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                  onClick={() => {
                    setIsCartOpen(false);
                    setIsResponseFormOpen(false);
                    setIsBookingOpen(false);
                    setIsRegisterUpdateOpen(false);
                    setIsAllRegisterUpdatesOpen(false);
                    setIsItemFormOpen(true);
                  }}
                  title={activeItemForm?.title || 'Item form'}
                  type="button"
                >
                  <SquareCheckIcon />
                  {itemFormItemCount > 0 && (
                    <span className="absolute -right-2 -top-2 grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-slate-950 px-1 text-[0.625rem] font-semibold leading-none text-white">
                      {itemFormItemCount}
                    </span>
                  )}
                </button>
              </div>
            )}
            {showHeaderCartButton && (
              <button
                aria-label={`Open cart with ${cartItemCount} items`}
                className="relative grid h-10 w-10 place-items-center rounded border border-slate-300 bg-white text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                onClick={() => {
                  setIsResponseFormOpen(false);
                  setIsBookingOpen(false);
                  setIsAllRegisterUpdatesOpen(false);
                  setIsCartOpen(true);
                }}
                title="Cart"
                type="button"
              >
                <CartIcon />
                {cartItemCount > 0 && (
                  <span className="absolute -right-2 -top-2 grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-slate-950 px-1 text-[0.625rem] font-semibold leading-none text-white">
                    {cartItemCount}
                  </span>
                )}
              </button>
            )}
            {showHeaderBookingButton && (
              <button
                aria-label={`Open booking request with ${bookingItemCount} ${isAccommodationBookingCategory ? 'rooms' : 'services'}`}
                className="relative grid h-10 w-10 place-items-center rounded border border-slate-300 bg-white text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                onClick={() => {
                  setIsResponseFormOpen(false);
                  setIsCartOpen(false);
                  setIsItemFormOpen(false);
                  setIsBookingOpen(true);
                }}
                title={responseFormButtonLabel}
                type="button"
              >
                <CalendarIcon />
                {bookingItemCount > 0 && (
                  <span className="absolute -right-2 -top-2 grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-slate-950 px-1 text-[0.625rem] font-semibold leading-none text-white">
                    {bookingItemCount}
                  </span>
                )}
              </button>
            )}
            {showCreateLinkButton && (
              <button
                aria-label="Create Link"
                className="inline-flex h-10 w-10 items-center justify-center gap-2 rounded bg-slate-950 px-0 text-sm font-semibold text-white transition hover:bg-slate-800 sm:w-auto sm:px-4"
                onClick={handleCreateLinkClick}
                title="Create Link"
                type="button"
              >
                <LinkIcon />
                <span className="hidden sm:inline">Create Link</span>
              </button>
            )}
            <AuthPanel user={user} />
          </div>
          <div className="col-span-2 lg:hidden">{gridSearchInput}</div>
        </div>
      </header>

      {requiresCredentials && !hasCredentialAccess && !publicAssetGrids.length ? (
        <section className={`mx-auto ${publicPageWidthClass} px-2 py-6 sm:px-8`}>
          {showHeroSection && renderShareHero({ showActions: false })}
          <div className="mx-auto max-w-md">
            {isInviteOnlyLink && !activeInviteOnlyForm && (
              <div className="rounded-lg border border-slate-200 bg-white p-6">
                <p className="label-caps">Invite only</p>
                <h1 className="mt-2 text-2xl font-semibold tracking-normal">Choose access option</h1>
                <div className="mt-5 grid gap-3">
                  <button
                    className="w-full rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                    onClick={() => setActiveInviteOnlyForm('request')}
                    type="button"
                  >
                    {inviteEntryActionLabel}
                  </button>
                  <button
                    className="w-full rounded border border-slate-950 bg-white px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-950 hover:text-white"
                    onClick={() => setActiveInviteOnlyForm('access')}
                    type="button"
                  >
                    I have an invite
                  </button>
                </div>
              </div>
            )}

            {isInviteOnlyLink && activeInviteOnlyForm === 'request' && (
              <form
                className="rounded-lg border border-slate-200 bg-white p-6"
                onSubmit={handleInviteRequestSubmit}
              >
                <p className="label-caps">{inviteEntryLabel}</p>
                <h1 className="mt-2 text-2xl font-semibold tracking-normal">{inviteEntryTitle}</h1>
                <label className="field-label" htmlFor="invite-request-email">Email address</label>
                <input
                  className="field"
                  id="invite-request-email"
                  type="email"
                  value={inviteRequestDraft.email}
                  onChange={(event) => setInviteRequestDraft({ ...inviteRequestDraft, email: event.target.value })}
                />
                <label className="field-label" htmlFor="invite-request-phone">Mobile number</label>
                <input
                  className="field"
                  id="invite-request-phone"
                  placeholder="+27 82 123 4567"
                  type="tel"
                  value={inviteRequestDraft.phone}
                  onChange={(event) => setInviteRequestDraft({ ...inviteRequestDraft, phone: event.target.value })}
                />
                <label className="field-label" htmlFor="invite-request-reason">Reason for invite</label>
                <textarea
                  className="field min-h-28 resize-none"
                  id="invite-request-reason"
                  maxLength="160"
                  value={inviteRequestDraft.reason}
                  onChange={(event) => setInviteRequestDraft({ ...inviteRequestDraft, reason: event.target.value.slice(0, 160) })}
                />
                <p className="mt-2 text-xs text-slate-500">{inviteRequestDraft.reason.length}/160</p>
                {inviteRequestError && <p className="mt-3 text-sm font-semibold text-red-700">{inviteRequestError}</p>}
                {inviteRequestStatus && <p className="mt-3 text-sm font-semibold text-emerald-700">{inviteRequestStatus}</p>}
                <button
                  className="mt-4 w-full rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                  disabled={isSubmittingInviteRequest}
                  type="submit"
                >
                  {isSubmittingInviteRequest ? 'Sending...' : inviteEntrySubmitLabel}
                </button>
                <button
                  className="mt-2 w-full rounded border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                  onClick={() => setActiveInviteOnlyForm('')}
                  type="button"
                >
                  Back
                </button>
              </form>
            )}

            {(!isInviteOnlyLink || activeInviteOnlyForm === 'access') && (
            <form
              className="rounded-lg border border-slate-200 bg-white p-6"
              onSubmit={handleVerifyAccess}
            >
              <p className="label-caps">Protected share</p>
              <h1 className="mt-2 text-2xl font-semibold tracking-normal">Open with invite</h1>
              <label className="field-label" htmlFor="share-access-email">Email address</label>
              <input
                className="field"
                id="share-access-email"
                type="email"
                value={accessDraft.email}
                onChange={(event) => setAccessDraft({ ...accessDraft, email: event.target.value })}
              />
              <label className="field-label" htmlFor="share-access-password">Password</label>
              <input
                className="field"
                id="share-access-password"
                type="password"
                value={accessDraft.password}
                onChange={(event) => setAccessDraft({ ...accessDraft, password: event.target.value })}
              />
              {accessError && <p className="mt-3 text-sm font-semibold text-red-700">{accessError}</p>}
              <button
                className="mt-4 w-full rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                disabled={isCheckingAccess}
                type="submit"
              >
                {isCheckingAccess ? 'Checking...' : 'Open share'}
              </button>
              {isInviteOnlyLink && (
                <button
                  className="mt-2 w-full rounded border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                  onClick={() => setActiveInviteOnlyForm('')}
                  type="button"
                >
                  Back
                </button>
              )}
            </form>
            )}
          </div>
        </section>
      ) : (
      <section className={`mx-auto ${publicPageWidthClass} px-2 py-6 sm:px-8`}>
        {hasResponseForm && !isAccommodationBookingForm && !showHeroSection && (
          <div className="mb-5 flex flex-wrap justify-end gap-2">
            {hasResponseForm && (
              <button
                className="rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                onClick={openResponseAction}
                type="button"
              >
                {responseFormButtonLabel}
              </button>
            )}
          </div>
        )}

        {showHeroSection && (
          renderShareHero()
        )}

        {isAccommodationBookingForm && !isDigitalServiceBookingCategory && (
          <section className="mb-5 rounded-lg border border-slate-300 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="grid grid-cols-2 gap-2 rounded border border-slate-200 bg-slate-50 p-1">
                    <button
                      aria-pressed={bookingMode === 'booking'}
                      className={`rounded px-3 py-2 text-sm font-semibold ${bookingMode === 'booking' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500'}`}
                      onClick={() => {
                        setBookingMode('booking');
                        setBookingStatus('');
                        setBookingError('');
                      }}
                      type="button"
                    >
                      Booking
                    </button>
                    <button
                      aria-pressed={bookingMode === 'request'}
                      className={`rounded px-3 py-2 text-sm font-semibold ${bookingMode === 'request' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500'}`}
                      onClick={() => {
                        setBookingMode('request');
                        setBookingItems([]);
                        setBookingStatus('');
                        setBookingError('');
                      }}
                      type="button"
                    >
                      Request
                    </button>
                  </div>
                  {bookingMode === 'booking' && (
                    <p className="text-sm font-semibold text-slate-500">
                      {bookingVisibleAssets.length} {isAccommodationBookingCategory ? 'place' : 'service'}{bookingVisibleAssets.length === 1 ? '' : 's'} available
                    </p>
                  )}
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-4 xl:grid-cols-7">
                  {isAccommodationBookingCategory ? (
                    <>
                      {(hasBookingField(CHECK_IN_FIELD_ID) || hasBookingField(CHECK_OUT_FIELD_ID)) && (
                        <div className="grid grid-cols-2 gap-3 md:contents">
                          {hasBookingField(CHECK_IN_FIELD_ID) && (
                            <div>
                              <label className="field-label mt-0" htmlFor="booking-filter-checkin">Check-in</label>
                              <input
                                className="field"
                                id="booking-filter-checkin"
                                min={today}
                                onChange={(event) => updateBookingFilter('checkInDate', event.target.value)}
                                type="date"
                                value={bookingFilter.checkInDate}
                              />
                            </div>
                          )}
                          {hasBookingField(CHECK_OUT_FIELD_ID) && (
                            <div>
                              <label className="field-label mt-0" htmlFor="booking-filter-checkout">Check-out</label>
                              <input
                                className="field"
                                id="booking-filter-checkout"
                                min={addDaysToDateValue(bookingFilter.checkInDate, 1) || today}
                                onChange={(event) => updateBookingFilter('checkOutDate', event.target.value)}
                                type="date"
                                value={bookingFilter.checkOutDate}
                              />
                            </div>
                          )}
                        </div>
                      )}
                      {(hasBookingField(ADULT_COUNT_FIELD_ID) || hasBookingField(KIDS_COUNT_FIELD_ID)) && (
                        <div className="grid grid-cols-2 gap-3 md:contents">
                          {hasBookingField(ADULT_COUNT_FIELD_ID) && (
                            <div>
                              <label className="field-label mt-0" htmlFor="booking-filter-adults">Adults</label>
                              <input
                                className="field"
                                id="booking-filter-adults"
                                min="0"
                                onChange={(event) => updateBookingFilter('adults', event.target.value)}
                                type="number"
                                value={bookingFilter.adults}
                              />
                            </div>
                          )}
                          {hasBookingField(KIDS_COUNT_FIELD_ID) && (
                            <div>
                              <label className="field-label mt-0" htmlFor="booking-filter-kids">Kids</label>
                              <input
                                className="field"
                                id="booking-filter-kids"
                                min="0"
                                onChange={(event) => updateBookingFilter('kids', event.target.value)}
                                type="number"
                                value={bookingFilter.kids}
                              />
                            </div>
                          )}
                        </div>
                      )}
                      {(hasBookingField(ROOMS_FIELD_ID) || hasBookingField(BEDS_FIELD_ID)) && (
                        <div className="grid grid-cols-2 gap-3 md:contents">
                          {hasBookingField(ROOMS_FIELD_ID) && (
                            <div>
                              <label className="field-label mt-0" htmlFor="booking-filter-rooms">Rooms</label>
                              <input
                                className="field"
                                id="booking-filter-rooms"
                                min="1"
                                onChange={(event) => updateBookingFilter('rooms', event.target.value)}
                                type="number"
                                value={bookingFilter.rooms}
                              />
                            </div>
                          )}
                          {hasBookingField(BEDS_FIELD_ID) && (
                            <div>
                              <label className="field-label mt-0" htmlFor="booking-filter-beds">Beds</label>
                              <input
                                className="field"
                                id="booking-filter-beds"
                                min="0"
                                onChange={(event) => updateBookingFilter('beds', event.target.value)}
                                type="number"
                                value={bookingFilter.beds}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {hasBookingField(SERVICE_CATEGORY_FIELD_ID) && (
                      <div>
                        <label className="field-label mt-0" htmlFor="booking-filter-service-category">Category</label>
                        <select
                          className="field"
                          id="booking-filter-service-category"
                          onChange={(event) => updateBookingFilter('serviceCategory', event.target.value)}
                          value={bookingFilter.serviceCategory}
                        >
                          <option value="">Choose category</option>
                          {normaliseOptionList(
                            bookingFields.find((field) => field.id === SERVICE_CATEGORY_FIELD_ID)?.options || ['Home', 'Car', 'Office', 'Transport'],
                          ).map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                      )}
                      {hasBookingField(SERVICE_TYPE_FIELD_ID) && (
                      <div>
                        <label className="field-label mt-0" htmlFor="booking-filter-service-type">Service</label>
                        <select
                          className="field"
                          id="booking-filter-service-type"
                          onChange={(event) => updateBookingFilter('serviceType', event.target.value)}
                          value={bookingFilter.serviceType}
                        >
                          <option value="">Choose service</option>
                          {(serviceOptionsByCategory[bookingFilter.serviceCategory] || normaliseOptionList(
                            bookingFields.find((field) => field.id === SERVICE_TYPE_FIELD_ID)?.options || [],
                          )).map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                      )}
                      {(hasBookingField(SERVICE_DATE_FIELD_ID) || hasBookingField(SERVICE_TIME_FIELD_ID)) && (
                      <div className="grid grid-cols-2 gap-3 md:contents">
                        {hasBookingField(SERVICE_DATE_FIELD_ID) && (
                        <div>
                          <label className="field-label mt-0" htmlFor="booking-filter-service-date">Date</label>
                          <input
                            className="field"
                            id="booking-filter-service-date"
                            min={today}
                            onChange={(event) => updateBookingFilter('serviceDate', event.target.value)}
                            type="date"
                            value={bookingFilter.serviceDate}
                          />
                        </div>
                        )}
                        {hasBookingField(SERVICE_TIME_FIELD_ID) && (
                        <div>
                          <label className="field-label mt-0" htmlFor="booking-filter-service-time">Time</label>
                          <input
                            className="field"
                            id="booking-filter-service-time"
                            onChange={(event) => updateBookingFilter('serviceTime', event.target.value)}
                            type="time"
                            value={bookingFilter.serviceTime}
                          />
                        </div>
                        )}
                      </div>
                      )}
                      {isTransportServiceRequest ? (
                        <div className="grid grid-cols-2 gap-3 md:contents">
                          {hasBookingField(PICKUP_ADDRESS_FIELD_ID) && (
                          <div>
                            <label className="field-label mt-0" htmlFor="booking-filter-pickup-address">Pickup</label>
                            <input
                              className="field"
                              id="booking-filter-pickup-address"
                              onChange={(event) => updateBookingFilter('pickupAddress', event.target.value)}
                              placeholder="Pickup address"
                              value={bookingFilter.pickupAddress}
                            />
                          </div>
                          )}
                          {hasBookingField(DROPOFF_ADDRESS_FIELD_ID) && (
                          <div>
                            <label className="field-label mt-0" htmlFor="booking-filter-dropoff-address">Drop-off</label>
                            <input
                              className="field"
                              id="booking-filter-dropoff-address"
                              onChange={(event) => updateBookingFilter('dropoffAddress', event.target.value)}
                              placeholder="Drop-off address"
                              value={bookingFilter.dropoffAddress}
                            />
                          </div>
                          )}
                        </div>
                      ) : hasBookingField(SERVICE_ADDRESS_FIELD_ID) ? (
                        <div>
                          <label className="field-label mt-0" htmlFor="booking-filter-service-address">Address</label>
                          <input
                            className="field"
                            id="booking-filter-service-address"
                            onChange={(event) => updateBookingFilter('serviceAddress', event.target.value)}
                            placeholder="Service address"
                            value={bookingFilter.serviceAddress}
                          />
                        </div>
                      ) : null}
                    </>
                  )}
                  {hasBookingField(AREA_FIELD_ID) && (
                  <div>
                    <label className="field-label mt-0" htmlFor="booking-filter-area">Area</label>
                    {areaOptions.length ? (
                      <select
                        className="field"
                        id="booking-filter-area"
                        onChange={(event) => updateBookingFilter('area', event.target.value)}
                        value={bookingFilter.area}
                      >
                        <option value="">Choose area</option>
                        {areaOptions.map((area) => (
                          <option key={area} value={area}>{area}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="field"
                        id="booking-filter-area"
                        onChange={(event) => updateBookingFilter('area', event.target.value)}
                        placeholder="Preferred area"
                        value={bookingFilter.area}
                      />
                    )}
                  </div>
                  )}
                </div>

                {bookingMode === 'request' && (
                  <button
                    className="mt-4 rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                    onClick={openBookingRequest}
                    type="button"
                  >
                    Complete Request
                  </button>
                )}
              </section>
          )}

        <AssetGridGallery
          activeGridId={activePublicGrid?.id || ''}
          assets={isAccommodationBookingForm ? bookingVisibleAssets : gridAccessibleAssets}
          assetFields={link.assetFields}
          layout={link.layout}
          columns={link.galleryColumns}
          enableBookingActions={isAccommodationBookingForm}
          focusedAssetId={focusedPublicAsset?.id || ''}
          searchQuery={activeGridSearch}
          link={{ ...link, assetGrids: publicAssetGrids }}
          permissions={recipientPermissions}
          showShareAction={!showHeroSection}
          onActiveGridChange={(gridId) => {
            setActivePublicGridId(gridId);
            setActiveGridSearch('');
            setIsResponseFormOpen(false);
            setIsItemFormOpen(false);
            setIsRegisterUpdateOpen(false);
            setIsCartOpen(false);
            setIsBookingOpen(false);
            setIsInquiryOpen(false);
            setActiveItemFormId('');
          }}
          onAddToCart={canUseCheckout && checkoutBelongsToActiveGrid ? handleAddToCart : undefined}
          onAddToBooking={isAccommodationBookingForm && bookingMode === 'booking' ? handleAddToBooking : undefined}
          addToCartDisabled={isAccommodationBookingForm && bookingMode === 'booking' ? !canBookSelectedPlace : false}
          onAddToItemForm={canUseInternalItemForms ? handleAddToItemForm : undefined}
          itemFormButtonText={activeItemForm?.buttonText}
          onAddUpdate={canAddRegisterUpdates ? handleOpenRegisterUpdate : undefined}
          onEnquire={handleOpenInquiry}
          onListInteraction={handleListInteraction}
          onShare={() => linkAnalyticsService.trackUniqueShare(link.id)}
          shareDescription={link.description}
          shareTitle={link.title}
          shareUrl={shareUrl}
          showListingFilters={!isAccommodationBookingForm}
          cartButtonLabel={isAccommodationBookingForm
            ? 'Book Now'
            : checkout.cartButtonLabel}
        />

        {isInquiryOpen && activeInquiryAsset && (
          <div
            className="fixed inset-0 z-50 bg-slate-950/40"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setIsInquiryOpen(false);
            }}
            role="presentation"
          >
            <aside
              aria-labelledby="inquiry-title"
              aria-modal="true"
              className="ml-auto flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-slate-200 bg-white shadow-2xl"
              role="dialog"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className="flex shrink-0 items-center justify-center overflow-hidden rounded border border-slate-200 bg-white"
                    style={drawerLogoContainerStyle}
                  >
                    <img
                      alt={`${link.owner || 'Sharabl'} logo`}
                      style={drawerLogoImageStyle}
                      src={formHeaderImage}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="label-caps">Listing inquiry</p>
                    <h2 className="mt-1 truncate text-lg font-semibold text-slate-950" id="inquiry-title">
                      Enquire about {itemTitle(activeInquiryAsset)}
                    </h2>
                  </div>
                </div>
                <button
                  aria-label="Close inquiry"
                  className="icon-button"
                  onClick={() => setIsInquiryOpen(false)}
                  type="button"
                >
                  x
                </button>
              </div>

              <form className="grid gap-4 p-5" onSubmit={handleInquirySubmit}>
                <div className="flex gap-3 rounded border border-slate-200 bg-slate-50 p-3">
                  {(activeInquiryAsset.preview || activeInquiryAsset.downloadUrl || activeInquiryAsset.listingLogoUrl) && (
                    <img
                      alt={`${itemTitle(activeInquiryAsset)} preview`}
                      className="h-16 w-20 shrink-0 rounded border border-slate-200 bg-white object-cover"
                      src={activeInquiryAsset.preview || activeInquiryAsset.downloadUrl || activeInquiryAsset.listingLogoUrl}
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-950">{itemTitle(activeInquiryAsset)}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {activeInquiryAsset.price && <span className="chip">{formatPrice(activeInquiryAsset.currency || link.currency || 'ZAR', activeInquiryAsset.price)}</span>}
                      {activeInquiryAsset.area && <span className="chip">{activeInquiryAsset.area}</span>}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="field-label mt-0" htmlFor="inquiry-name">Name</label>
                  <input
                    className="field"
                    id="inquiry-name"
                    onChange={(event) => updateInquiryAnswer('name', event.target.value)}
                    placeholder="Full name"
                    required
                    value={inquiryAnswers.name}
                  />
                </div>
                <div>
                  <label className="field-label mt-0" htmlFor="inquiry-email">Email</label>
                  <input
                    className="field"
                    id="inquiry-email"
                    onChange={(event) => updateInquiryAnswer('email', event.target.value)}
                    placeholder="name@example.com"
                    required
                    type="email"
                    value={inquiryAnswers.email}
                  />
                </div>
                <div>
                  <label className="field-label mt-0" htmlFor="inquiry-phone">Phone</label>
                  <input
                    className="field"
                    id="inquiry-phone"
                    onChange={(event) => updateInquiryAnswer('phone', event.target.value)}
                    placeholder="+27 00 000 0000"
                    required
                    type="tel"
                    value={inquiryAnswers.phone}
                  />
                </div>
                <div>
                  <label className="field-label mt-0" htmlFor="inquiry-message">Message</label>
                  <textarea
                    className="field min-h-28 resize-none"
                    id="inquiry-message"
                    onChange={(event) => updateInquiryAnswer('message', event.target.value)}
                    required
                    value={inquiryAnswers.message}
                  />
                </div>

                {inquiryError && (
                  <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{inquiryError}</p>
                )}
                {inquiryStatus && (
                  <p className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{inquiryStatus}</p>
                )}

                <button
                  className="justify-self-start rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                  disabled={isSubmittingInquiry}
                  type="submit"
                >
                  {isSubmittingInquiry ? 'Sending...' : 'Send inquiry'}
                </button>
              </form>
            </aside>
          </div>
        )}

        {canUseCheckout && checkoutBelongsToActiveGrid && hasCheckoutGrid && isCartOpen && (
          <div
            className="fixed inset-0 z-50 bg-slate-950/40"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setIsCartOpen(false);
            }}
            role="presentation"
          >
            <aside
              aria-labelledby="cart-title"
              aria-modal="true"
              className={`ml-auto flex h-full w-full max-w-xl flex-col overflow-y-auto border-l bg-white shadow-2xl ${isPosCheckout ? 'border-emerald-200' : 'border-slate-200'}`}
              role="dialog"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className="flex shrink-0 items-center justify-center overflow-hidden bg-white"
                    style={drawerLogoContainerStyle}
                  >
                    <img
                      alt={`${link.owner || 'Sharabl'} logo`}
                      style={drawerLogoImageStyle}
                      src={formHeaderImage}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="label-caps">{checkout.drawerEyebrow?.trim() || 'Store checkout'}</p>
                    <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
                      <h2 className="truncate text-lg font-semibold text-slate-950" id="cart-title">
                        {checkout.drawerTitle?.trim() || 'Cart'} ({cartItemCount})
                      </h2>
                      {isPosCheckout && (
                        <span className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-[0.625rem] font-semibold uppercase leading-none text-emerald-700">
                          In-store sale
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  aria-label="Close cart"
                  className="icon-button"
                  onClick={() => setIsCartOpen(false)}
                  type="button"
                >
                  x
                </button>
              </div>

              <div className="grid gap-5 p-5">
                {cartItems.length ? (
                  <div className="grid gap-3">
                    {cartItems.map((item) => (
                      <div className="rounded border border-slate-200 bg-slate-50 p-3" key={item.assetId}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 gap-3">
                            {item.imageUrl && (
                              <img
                                alt={`${item.title} preview`}
                                className="h-16 w-20 shrink-0 rounded border border-slate-200 bg-white object-cover"
                                src={item.imageUrl}
                              />
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-950">{item.title}</p>
                              <p className="mt-1 text-xs font-semibold text-slate-500">
                                {item.price ? formatPrice(item.currency, item.price) : 'Price on request'}
                              </p>
                            </div>
                          </div>
                          <button
                            className="text-sm font-semibold text-slate-500 transition hover:text-red-700"
                            onClick={() => updateCartQuantity(item.assetId, 0)}
                            type="button"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="mt-3 grid grid-cols-[7rem_1fr] items-end gap-3">
                          <div>
                            <label className="field-label mt-0" htmlFor={`cart-quantity-${item.assetId}`}>Quantity</label>
                            <input
                              className="field"
                              id={`cart-quantity-${item.assetId}`}
                              min="1"
                              onChange={(event) => updateCartQuantity(item.assetId, event.target.value)}
                              type="number"
                              value={item.quantity}
                            />
                          </div>
                          <p className="pb-2 text-right text-sm font-semibold text-slate-950">
                            {formatMoney(item.currency, item.lineTotal)}
                          </p>
                        </div>
                        {(item.availableSizes?.length || item.availableColors?.length) && (
                          <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            {item.availableSizes?.length > 0 && (
                              <div>
                                <label className="field-label mt-0" htmlFor={`cart-size-${item.assetId}`}>
                                  Size
                                </label>
                                <select
                                  className="field"
                                  id={`cart-size-${item.assetId}`}
                                  onChange={(event) => updateCartOption(item.assetId, 'selectedSize', event.target.value)}
                                  required
                                  value={item.selectedSize || ''}
                                >
                                  <option value="">Choose size</option>
                                  {item.availableSizes.map((size) => (
                                    <option key={size} value={size}>{size}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                            {item.availableColors?.length > 0 && (
                              <div>
                                <label className="field-label mt-0" htmlFor={`cart-color-${item.assetId}`}>
                                  Color
                                </label>
                                <select
                                  className="field"
                                  id={`cart-color-${item.assetId}`}
                                  onChange={(event) => updateCartOption(item.assetId, 'selectedColor', event.target.value)}
                                  required
                                  value={item.selectedColor || ''}
                                >
                                  <option value="">Choose color</option>
                                  {item.availableColors.map((color) => (
                                    <option key={color} value={color}>{color}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="grid gap-2 border-t border-slate-200 pt-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-600">Subtotal</span>
                        <span className="text-sm font-semibold text-slate-950">
                          {formatMoney(cartCurrency, cartTotal)}
                        </span>
                      </div>
                      {deliveryFee > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-slate-600">Delivery</span>
                          <span className="text-sm font-semibold text-slate-950">
                            {formatMoney(cartCurrency, deliveryFee)}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-600">Total</span>
                        <strong className="text-xl font-semibold text-slate-950">
                          {formatMoney(cartCurrency, checkoutTotal)}
                        </strong>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500">
                    {checkout.emptyCartMessage?.trim() || 'Add store items to start an order.'}
                  </div>
                )}

                <form
                  className={`grid gap-4 ${isPosCheckout ? 'rounded border border-emerald-200 bg-emerald-50/30 p-4' : 'border-t border-slate-200 pt-5'}`}
                  onSubmit={handleCheckout}
                >
                  <div>
                    <p className="label-caps">{checkoutDetailsLabel}</p>
                    <h3 className="mt-2 text-lg font-semibold text-slate-950">
                      {checkout.formTitle?.trim() || 'Send order'}
                    </h3>
                  </div>

                  {canUsePosCheckout && (
                    <div className={`grid gap-2 rounded border border-slate-200 bg-slate-50 p-1 ${canUseOnlineCheckout ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      <button
                        className={`rounded px-3 py-2 text-sm font-semibold ${isPosCheckout ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500'}`}
                        onClick={() => setCheckoutMode('pos')}
                        type="button"
                      >
                        In-store sale
                      </button>
                      {canUseOnlineCheckout && (
                        <button
                          className={`rounded px-3 py-2 text-sm font-semibold ${!isPosCheckout ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500'}`}
                          onClick={() => setCheckoutMode('online')}
                          type="button"
                        >
                          Online order
                        </button>
                      )}
                    </div>
                  )}

                  {isRestaurantCheckout && !isPosCheckout && canUseOnlineCheckout && (
                    <div className="grid grid-cols-2 gap-2 rounded border border-slate-200 bg-slate-50 p-1">
                      <button
                        className={`rounded px-3 py-2 text-sm font-semibold ${isOnsiteOrder ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500'}`}
                        onClick={() => setCheckoutMode('onsite')}
                        type="button"
                      >
                        Onsite
                      </button>
                      <button
                        className={`rounded px-3 py-2 text-sm font-semibold ${isOffsiteOrder ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500'}`}
                        onClick={() => setCheckoutMode('offsite')}
                        type="button"
                      >
                        Offsite
                      </button>
                    </div>
                  )}

                  {isPosCheckout ? (
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="checkout-payment-method">
                        Payment type
                      </label>
                      <select
                        className="field"
                        id="checkout-payment-method"
                        onChange={(event) => setCustomerDraft({ ...customerDraft, paymentMethod: event.target.value })}
                        value={customerDraft.paymentMethod}
                      >
                        <option value="Cash">Cash</option>
                        <option value="Card">Card</option>
                        <option value="Transfer">Transfer</option>
                      </select>
                    </div>
                  ) : isOnsiteOrder ? (
                    <>
                      <div>
                        <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="checkout-table-number">
                          {checkout.tableNumberLabel?.trim() || 'Table number'} <span className="text-red-600">*</span>
                        </label>
                        <input
                          className="field"
                          id="checkout-table-number"
                          onChange={(event) => setCustomerDraft({ ...customerDraft, tableNumber: event.target.value })}
                          required
                          value={customerDraft.tableNumber}
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="checkout-notes">
                          {checkout.notesLabel?.trim() || 'Notes'}
                        </label>
                        <textarea
                          className="field min-h-24 resize-none"
                          id="checkout-notes"
                          onChange={(event) => setCustomerDraft({ ...customerDraft, notes: event.target.value })}
                          value={customerDraft.notes}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="checkout-name">
                          {checkout.nameLabel?.trim() || 'Name'} <span className="text-red-600">*</span>
                        </label>
                        <input
                          className="field"
                          id="checkout-name"
                          onChange={(event) => setCustomerDraft({ ...customerDraft, name: event.target.value })}
                          required
                          value={customerDraft.name}
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="checkout-email">
                          {checkout.emailLabel?.trim() || 'Email'} <span className="text-red-600">*</span>
                        </label>
                        <input
                          className="field"
                          id="checkout-email"
                          onChange={(event) => setCustomerDraft({ ...customerDraft, email: event.target.value })}
                          required
                          type="email"
                          value={customerDraft.email}
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="checkout-phone">
                          {checkout.phoneLabel?.trim() || 'Phone'}
                        </label>
                        <input
                          className="field"
                          id="checkout-phone"
                          onChange={(event) => setCustomerDraft({ ...customerDraft, phone: event.target.value })}
                          type="tel"
                          value={customerDraft.phone}
                        />
                      </div>
                      {isOffsiteOrder && (
                        <div>
                          <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="checkout-delivery-address">
                            {checkout.addressLabel?.trim() || 'Delivery address'} <span className="text-red-600">*</span>
                          </label>
                          <textarea
                            className="field min-h-24 resize-none"
                            id="checkout-delivery-address"
                            onChange={(event) => setCustomerDraft({ ...customerDraft, deliveryAddress: event.target.value })}
                            required
                            value={customerDraft.deliveryAddress}
                          />
                        </div>
                      )}
                      <div>
                        <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="checkout-notes">
                          {checkout.notesLabel?.trim() || 'Notes'}
                        </label>
                        <textarea
                          className="field min-h-24 resize-none"
                          id="checkout-notes"
                          onChange={(event) => setCustomerDraft({ ...customerDraft, notes: event.target.value })}
                          value={customerDraft.notes}
                        />
                      </div>
                    </>
                  )}

                  {checkoutError && (
                    <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{checkoutError}</p>
                  )}
                  {checkoutStatus && (
                    <p className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{checkoutStatus}</p>
                  )}
                  {checkoutPaymentDetails && (
                    <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-900">
                      <p className="font-semibold text-emerald-950">Offline payment details</p>
                      {checkout.paymentInstructions?.trim() && (
                        <p className="mt-2 whitespace-pre-wrap leading-6">{checkout.paymentInstructions.trim()}</p>
                      )}
                      <dl className="mt-3 grid gap-2">
                        {checkout.bankName?.trim() && (
                          <div>
                            <dt className="text-xs font-semibold uppercase text-emerald-700">Bank</dt>
                            <dd className="mt-0.5 text-slate-950">{checkout.bankName.trim()}</dd>
                          </div>
                        )}
                        {checkout.accountName?.trim() && (
                          <div>
                            <dt className="text-xs font-semibold uppercase text-emerald-700">Account name</dt>
                            <dd className="mt-0.5 text-slate-950">{checkout.accountName.trim()}</dd>
                          </div>
                        )}
                        {checkout.accountNumber?.trim() && (
                          <div>
                            <dt className="text-xs font-semibold uppercase text-emerald-700">Account number</dt>
                            <dd className="mt-0.5 text-slate-950">{checkout.accountNumber.trim()}</dd>
                          </div>
                        )}
                        {checkout.branchCode?.trim() && (
                          <div>
                            <dt className="text-xs font-semibold uppercase text-emerald-700">Branch code</dt>
                            <dd className="mt-0.5 text-slate-950">{checkout.branchCode.trim()}</dd>
                          </div>
                        )}
                        {checkout.accountType?.trim() && (
                          <div>
                            <dt className="text-xs font-semibold uppercase text-emerald-700">Account type</dt>
                            <dd className="mt-0.5 text-slate-950">{checkout.accountType.trim()}</dd>
                          </div>
                        )}
                        <div>
                          <dt className="text-xs font-semibold uppercase text-emerald-700">Payment reference</dt>
                          <dd className="mt-0.5 font-semibold text-slate-950">{checkoutPaymentDetails.reference}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-semibold uppercase text-emerald-700">Amount</dt>
                          <dd className="mt-0.5 font-semibold text-slate-950">
                            {formatMoney(checkoutPaymentDetails.currency, checkoutPaymentDetails.amount)}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  )}

                  <button
                    className="justify-self-start rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                    disabled={isCheckingOut || !cartItems.length}
                    type="submit"
                  >
                    {isCheckingOut ? 'Sending...' : checkoutSubmitLabel}
                  </button>
                </form>
              </div>
            </aside>
          </div>
        )}

        {isAccommodationBookingForm && isBookingOpen && (
          <div
            className="fixed inset-0 z-50 bg-slate-950/40"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setIsBookingOpen(false);
            }}
            role="presentation"
          >
            <aside
              aria-labelledby="booking-title"
              aria-modal="true"
              className="ml-auto flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-slate-200 bg-white shadow-2xl"
              role="dialog"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className="flex shrink-0 items-center justify-center overflow-hidden rounded border border-slate-200 bg-white"
                    style={drawerLogoContainerStyle}
                  >
                    <img
                      alt={`${link.owner || 'Sharabl'} logo`}
                      style={drawerLogoImageStyle}
                      src={formHeaderImage}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="label-caps">{bookingMode === 'request' ? `${bookingCategoryLabel} request` : 'Booking'}</p>
                    <h2 className="mt-1 truncate text-lg font-semibold text-slate-950" id="booking-title">
                      {bookingMode === 'request' ? 'Complete request' : gridScopedForm?.title || `${bookingCategoryLabel} booking`}
                    </h2>
                  </div>
                </div>
                <button
                  aria-label="Close booking request"
                  className="icon-button"
                  onClick={() => setIsBookingOpen(false)}
                  type="button"
                >
                  x
                </button>
              </div>

              <form className="grid gap-5 p-5" onSubmit={handleBookingSubmit}>
                <div className="grid gap-2 rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                  <div className="flex flex-wrap gap-2">
                    {bookingFilter.area && <span className="chip">{bookingFilter.area}</span>}
                    {isAccommodationBookingCategory ? (
                      <>
                        {bookingFilter.checkInDate && <span className="chip">Check-in: {bookingFilter.checkInDate}</span>}
                        {bookingFilter.checkOutDate && <span className="chip">Check-out: {bookingFilter.checkOutDate}</span>}
                        <span className="chip">{bookingFilter.adults || 0} adult{Number(bookingFilter.adults) === 1 ? '' : 's'}</span>
                        <span className="chip">{bookingFilter.kids || 0} kid{Number(bookingFilter.kids) === 1 ? '' : 's'}</span>
                        <span className="chip">{bookingFilter.rooms || 0} room{Number(bookingFilter.rooms) === 1 ? '' : 's'}</span>
                        <span className="chip">{bookingFilter.beds || 0} bed{Number(bookingFilter.beds) === 1 ? '' : 's'}</span>
                      </>
                    ) : (
                      <>
                        {bookingFilter.serviceCategory && <span className="chip">{bookingFilter.serviceCategory}</span>}
                        {bookingFilter.serviceType && <span className="chip">{bookingFilter.serviceType}</span>}
                        {bookingFilter.serviceDate && <span className="chip">Date: {bookingFilter.serviceDate}</span>}
                        {bookingFilter.serviceTime && <span className="chip">Time: {bookingFilter.serviceTime}</span>}
                        {bookingFilter.serviceAddress && <span className="chip">{bookingFilter.serviceAddress}</span>}
                        {bookingFilter.pickupAddress && <span className="chip">Pickup: {bookingFilter.pickupAddress}</span>}
                        {bookingFilter.dropoffAddress && <span className="chip">Drop-off: {bookingFilter.dropoffAddress}</span>}
                      </>
                    )}
                  </div>
                </div>

                {bookingMode === 'booking' && bookingItems.length ? (
                  <div className="grid gap-3">
                    {bookingItems.map((item) => (
                      <div className="rounded border border-slate-200 bg-slate-50 p-3" key={item.assetId}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 gap-3">
                            {item.imageUrl && (
                              <img
                                alt={`${item.title} preview`}
                                className="h-16 w-20 shrink-0 rounded border border-slate-200 bg-white object-cover"
                                src={item.imageUrl}
                              />
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-950">{item.title}</p>
                              {isAccommodationBookingCategory && (
                                <p className="mt-1 text-xs font-semibold text-slate-500">
                                  {item.availableRooms} room{item.availableRooms === 1 ? '' : 's'} available
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            className="text-sm font-semibold text-slate-500 transition hover:text-red-700"
                            onClick={() => updateBookingRooms(item.assetId, 0)}
                            type="button"
                          >
                            Remove
                          </button>
                        </div>
                        {isAccommodationBookingCategory && (
                        <div className="mt-3 grid max-w-xs grid-cols-2 gap-3">
                          <div>
                            <label className="field-label mt-0" htmlFor={`booking-rooms-${item.assetId}`}>Rooms</label>
                            <input
                              className="field"
                              id={`booking-rooms-${item.assetId}`}
                              min="1"
                              onChange={(event) => updateBookingRooms(item.assetId, event.target.value)}
                              type="number"
                              value={item.rooms}
                            />
                          </div>
                          <div>
                            <label className="field-label mt-0" htmlFor={`booking-beds-${item.assetId}`}>Beds</label>
                            <input
                              className="field"
                              id={`booking-beds-${item.assetId}`}
                              min="0"
                              onChange={(event) => updateBookingItemBeds(item.assetId, event.target.value)}
                              type="number"
                              value={item.beds ?? 0}
                            />
                          </div>
                        </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : bookingMode === 'booking' ? (
                  <p className="rounded border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                    Use the booking button on a {isAccommodationBookingCategory ? 'place' : 'service'} to add it here.
                  </p>
                ) : null}

                <div className="grid gap-4 border-t border-slate-200 pt-5">
                  {renderBookingFields()}
                </div>

                {bookingError && (
                  <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{bookingError}</p>
                )}
                {bookingStatus && (
                  <p className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{bookingStatus}</p>
                )}

                <button
                  className="justify-self-start rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                  disabled={isSubmittingBooking || (bookingMode === 'booking' && !bookingItems.length)}
                  type="submit"
                >
                  {isSubmittingBooking ? 'Sending...' : bookingMode === 'request' ? 'Submit request' : gridScopedForm?.submitLabel || 'Submit booking'}
                </button>
              </form>
            </aside>
          </div>
        )}

        {isAllRegisterUpdatesOpen && (
          <div
            className="fixed inset-0 z-50 bg-slate-950/40"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setIsAllRegisterUpdatesOpen(false);
            }}
            role="presentation"
          >
            <aside
              aria-labelledby="all-register-updates-title"
              aria-modal="true"
              className="ml-auto flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-slate-200 bg-white shadow-2xl"
              role="dialog"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className="flex shrink-0 items-center justify-center overflow-hidden rounded border border-slate-200 bg-white"
                    style={drawerLogoContainerStyle}
                  >
                    <img
                      alt={`${link.owner || 'Sharabl'} logo`}
                      style={drawerLogoImageStyle}
                      src={formHeaderImage}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="label-caps">Register</p>
                    <h2 className="mt-1 truncate text-lg font-semibold text-slate-950" id="all-register-updates-title">
                      Updates
                    </h2>
                  </div>
                </div>
                <button
                  aria-label="Close updates"
                  className="icon-button"
                  onClick={() => setIsAllRegisterUpdatesOpen(false)}
                  type="button"
                >
                  x
                </button>
              </div>

              <div className="grid gap-4 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-slate-950">All updates</h3>
                  {isLoadingAllRegisterUpdates && <span className="text-xs font-semibold text-slate-500">Loading...</span>}
                </div>
                {registerUpdateError && (
                  <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{registerUpdateError}</p>
                )}
                <div className="grid gap-3">
                  {allRegisterUpdates.map(renderRegisterUpdateRecord)}
                  {!isLoadingAllRegisterUpdates && !allRegisterUpdates.length && (
                    <p className="rounded border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                      No register updates have been added.
                    </p>
                  )}
                </div>
              </div>
            </aside>
          </div>
        )}

        {isRegisterUpdateOpen && activeRegisterAsset && (
          <div
            className="fixed inset-0 z-50 bg-slate-950/40"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setIsRegisterUpdateOpen(false);
            }}
            role="presentation"
          >
            <aside
              aria-labelledby="register-update-title"
              aria-modal="true"
              className="ml-auto flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-slate-200 bg-white shadow-2xl"
              role="dialog"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className="flex shrink-0 items-center justify-center overflow-hidden rounded border border-slate-200 bg-white"
                    style={drawerLogoContainerStyle}
                  >
                    <img
                      alt={`${link.owner || 'Sharabl'} logo`}
                      style={drawerLogoImageStyle}
                      src={formHeaderImage}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="label-caps">Register update</p>
                    <h2 className="mt-1 truncate text-lg font-semibold text-slate-950" id="register-update-title">
                      {activeRegisterAsset.title || activeRegisterAsset.name || 'Register item'}
                    </h2>
                  </div>
                </div>
                <button
                  aria-label="Close update form"
                  className="icon-button"
                  onClick={() => setIsRegisterUpdateOpen(false)}
                  type="button"
                >
                  x
                </button>
              </div>

              <div className="grid gap-5 p-5">
                <form className="grid gap-4" onSubmit={handleRegisterUpdateSubmit}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="field-label mt-0" htmlFor="register-update-type">Update type</label>
                      <select
                        className="field"
                        id="register-update-type"
                        value={registerUpdateDraft.updateType}
                        onChange={(event) => setRegisterUpdateDraft({
                          ...registerUpdateDraft,
                          updateType: event.target.value,
                        })}
                      >
                        {registerUpdateTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="field-label mt-0" htmlFor="register-update-datetime">DateTime</label>
                      <input
                        className="field"
                        id="register-update-datetime"
                        type="datetime-local"
                        value={registerUpdateDraft.dateTime}
                        onChange={(event) => setRegisterUpdateDraft({
                          ...registerUpdateDraft,
                          dateTime: event.target.value,
                        })}
                      />
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="field-label mt-0" htmlFor="register-update-quantity">Quantity</label>
                      <input
                        className="field"
                        id="register-update-quantity"
                        inputMode="decimal"
                        placeholder="0"
                        type="number"
                        value={registerUpdateDraft.quantity}
                        onChange={(event) => setRegisterUpdateDraft({
                          ...registerUpdateDraft,
                          quantity: event.target.value,
                        })}
                      />
                    </div>
                    <div>
                      <p className="field-label mt-0" id="register-update-status-label">Status</p>
                      <div
                        aria-labelledby="register-update-status-label"
                        className="grid grid-cols-3 gap-2 rounded border border-slate-200 bg-slate-50 p-1"
                        role="group"
                      >
                        {registerStatusOptions.map((option) => (
                          <button
                            aria-pressed={registerUpdateDraft.status === option.value}
                            className={`rounded border px-3 py-3 text-sm font-bold transition ${option.className} ${
                              registerUpdateDraft.status === option.value ? 'ring-2 ring-slate-950 ring-offset-2' : 'opacity-80 hover:opacity-100'
                            }`}
                            key={option.value}
                            onClick={() => setRegisterUpdateDraft({
                              ...registerUpdateDraft,
                              status: registerUpdateDraft.status === option.value ? '' : option.value,
                            })}
                            title={option.title}
                            type="button"
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="field-label mt-0" htmlFor="register-update-message">Update</label>
                    <textarea
                      className="field min-h-32"
                      id="register-update-message"
                      placeholder="Add a concise update for this register item."
                      required
                      value={registerUpdateDraft.message}
                      onChange={(event) => setRegisterUpdateDraft({
                        ...registerUpdateDraft,
                        message: event.target.value,
                      })}
                    />
                  </div>

                  {registerUpdateError && (
                    <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{registerUpdateError}</p>
                  )}
                  {registerUpdateStatus && (
                    <p className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{registerUpdateStatus}</p>
                  )}

                  <button
                    className="justify-self-start rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                    disabled={isDemoMode || isSubmittingRegisterUpdate || !registerUpdateDraft.message.trim()}
                    type="submit"
                  >
                    {isDemoMode ? 'Submission disabled in demo' : isSubmittingRegisterUpdate ? 'Adding...' : 'Add update'}
                  </button>
                </form>

                <section className="border-t border-slate-200 pt-5">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-slate-950">Updates</h3>
                    {isLoadingRegisterUpdates && <span className="text-xs font-semibold text-slate-500">Loading...</span>}
                  </div>
                  <div className="mt-3 grid gap-3">
                    {registerUpdates.map(renderRegisterUpdateRecord)}
                    {!isLoadingRegisterUpdates && !registerUpdates.length && (
                      <p className="rounded border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                        No updates have been added for this item.
                      </p>
                    )}
                  </div>
                </section>
              </div>
            </aside>
          </div>
        )}

        {canUseInternalItemForms && isItemFormOpen && (
          <div
            className="fixed inset-0 z-50 bg-slate-950/40"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setIsItemFormOpen(false);
            }}
            role="presentation"
          >
            <aside
              aria-labelledby="item-form-title"
              aria-modal="true"
              className="ml-auto flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-slate-200 bg-white shadow-2xl"
              role="dialog"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className="flex shrink-0 items-center justify-center overflow-hidden rounded border border-slate-200 bg-white"
                    style={drawerLogoContainerStyle}
                  >
                    <img
                      alt={`${link.owner || 'Sharabl'} logo`}
                      style={drawerLogoImageStyle}
                      src={formHeaderImage}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="label-caps">Internal item form</p>
                    <h2 className="mt-1 truncate text-lg font-semibold text-slate-950" id="item-form-title">
                      {activeItemForm?.title || 'Item form'}
                    </h2>
                  </div>
                </div>
                <button
                  aria-label="Close item form"
                  className="icon-button"
                  onClick={() => setIsItemFormOpen(false)}
                  type="button"
                >
                  x
                </button>
              </div>

              <form className="grid gap-4 p-5" onSubmit={handleItemFormSubmit}>
                {isDemoMode && (
                  <p className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                    Demo mode is active. You can try this form, but it cannot be submitted.
                  </p>
                )}
                {enabledItemForms.length > 1 && (
                  <div>
                    <label className="field-label mt-0" htmlFor="active-item-form">Form</label>
                    <select
                      className="field"
                      id="active-item-form"
                      onChange={(event) => {
                        setActiveItemFormId(event.target.value);
                        setItemFormItems([]);
                        setItemFormOrderDraft(defaultItemFormOrderDraft());
                        setItemFormError('');
                        setItemFormStatus('');
                      }}
                      value={activeItemForm?.id || ''}
                    >
                      {enabledItemForms.map((form) => (
                        <option key={form.id} value={form.id}>{form.title}</option>
                      ))}
                    </select>
                  </div>
                )}

                {isOrderingForm(activeItemForm) && (
                  <div>
                    <label className="field-label mt-0" htmlFor="item-form-order-required-by">Required by</label>
                    <input
                      className="field"
                      id="item-form-order-required-by"
                      onChange={(event) => updateItemFormOrderDraft('requiredBy', event.target.value)}
                      type="date"
                      value={itemFormOrderDraft.requiredBy}
                    />
                  </div>
                )}

                {itemFormItems.map((item) => (
                  <article className="rounded border border-slate-200 bg-slate-50 p-3" key={item.itemId}>
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-3">
                        {renderItemFormItemImage(item)}
                        <h3 className="min-w-0 text-sm font-semibold text-slate-950">{item.itemTitle}</h3>
                      </div>
                      <button
                        className="text-sm font-semibold text-red-700"
                        onClick={() => removeItemFormItem(item.itemId)}
                        type="button"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid gap-3">
                      {(activeItemForm?.itemFields || []).map((field) => (
                        <div key={field.id}>
                          {field.type !== 'checkbox' && (
                            <label className="field-label mt-0" htmlFor={`share-item-form-${item.itemId}-${field.id}`}>
                              {field.label}
                              {field.required && <span className="text-red-600"> *</span>}
                            </label>
                          )}
                          {renderItemFormInput(item, field)}
                        </div>
                      ))}
                    </div>
                  </article>
                ))}

                {isOrderingForm(activeItemForm) && itemFormItems.length > 0 && (
                  <div className="grid gap-3 rounded border border-slate-200 bg-slate-50 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-600">Order total</span>
                      <strong className="text-lg font-semibold text-slate-950">
                        {formatMoney(link.currency || 'ZAR', itemFormOrderTotal)}
                      </strong>
                    </div>
                    <div>
                      <label className="field-label" htmlFor="item-form-order-status">Status *</label>
                      <select
                        className="field bg-white"
                        id="item-form-order-status"
                        onChange={(event) => updateItemFormOrderDraft('status', event.target.value)}
                        required
                        value={itemFormOrderDraft.status}
                      >
                        {['Requested', 'Approved', 'Ordered', 'Back ordered', 'Cancelled'].map((status) => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="field-label" htmlFor="item-form-order-notes">Notes</label>
                      <textarea
                        className="field min-h-24 resize-none bg-white"
                        id="item-form-order-notes"
                        onChange={(event) => updateItemFormOrderDraft('notes', event.target.value)}
                        placeholder="Order details, supplier, or follow-up"
                        value={itemFormOrderDraft.notes}
                      />
                    </div>
                  </div>
                )}

                {!itemFormItems.length && (
                  <p className="rounded border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                    Use the item form button on an item card to add it here.
                  </p>
                )}

                {itemFormError && (
                  <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{itemFormError}</p>
                )}
                {itemFormStatus && (
                  <p className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{itemFormStatus}</p>
                )}

                <button
                  className="justify-self-start rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                  disabled={isDemoMode || isSubmittingItemForm || !itemFormItems.length}
                  type="submit"
                >
                  {isDemoMode ? 'Submission disabled in demo' : isSubmittingItemForm ? 'Submitting...' : activeItemForm?.submitLabel || 'Submit item form'}
                </button>
              </form>
            </aside>
          </div>
        )}

        {hasResponseForm && isResponseFormOpen && (
          <div
            className="fixed inset-0 z-50 bg-slate-950/40"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeResponseForm();
            }}
            role="presentation"
          >
            <aside
              aria-labelledby="response-form-title"
              aria-modal="true"
              className="ml-auto flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-slate-200 bg-white shadow-2xl"
              role="dialog"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className="flex shrink-0 items-center justify-center overflow-hidden bg-white"
                    style={drawerLogoContainerStyle}
                  >
                    <img
                      alt={`${link.owner || 'Sharabl'} logo`}
                      style={drawerLogoImageStyle}
                      src={formHeaderImage}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="label-caps">Response</p>
                    <h2 className="mt-1 truncate text-lg font-semibold text-slate-950" id="response-form-title">
                      Response form
                    </h2>
                  </div>
                </div>
                <button
                  aria-label="Close response form"
                  className="icon-button"
                  onClick={closeResponseForm}
                  type="button"
                >
                  x
                </button>
              </div>
              <div className="p-5">
                <ShareFormRenderer link={{ ...link, form: gridScopedForm }} className="border-0 p-0 shadow-none" />
              </div>
            </aside>
          </div>
        )}
      </section>
      )}
      {renderPublicFooter()}
    </main>
  );
}

export default PublicSharePage;
